import { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Plus, Calendar as CalendarIcon, Clock, Trash2, Edit, ChevronLeft, ChevronRight, Video, Users, X, Play, Check, Repeat, RefreshCw, Settings, QrCode } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import Calendar from 'react-calendar';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isSameMonth, startOfWeek, endOfWeek, addMonths, subMonths, addWeeks, subWeeks, addYears, parseISO } from 'date-fns';
import { schedulesAPI, videosAPI, bookingsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useThumbnails } from '../hooks/useThumbnails';
import { DatePicker, TimePicker } from '../components/DateTimePicker';
import { CustomDropdown } from '../components/CustomDropdown';
import { useIsMobile } from '../hooks/useIsMobile';
import toast from 'react-hot-toast';
import 'react-calendar/dist/Calendar.css';

const CATEGORIES = [
  { value: 'yoga', label: 'Yoga', icon: '🧘' },
  { value: 'dance', label: 'Dance', icon: '💃' },
  { value: 'martial_arts', label: 'Martial Arts', icon: '🥋' },
  { value: 'fitness', label: 'Fitness', icon: '💪' },
  { value: 'pilates', label: 'Pilates', icon: '🤸' },
  { value: 'meditation', label: 'Meditation', icon: '🧘' },
  { value: 'other', label: 'Other', icon: '⭐' }
];

// Helper function to convert 24-hour time to 12-hour AM/PM format
function formatTime12Hour(timeString) {
  if (!timeString) return 'TBD';
  
  // Handle both HH:MM and HH:MM:SS formats
  const [hours, minutes] = timeString.split(':').map(Number);
  
  if (isNaN(hours) || isNaN(minutes)) return timeString;
  
  const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  const ampm = hours < 12 ? 'AM' : 'PM';
  
  return `${hour12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
}

// Generate human-readable recurrence description
function getRecurrenceDescription(recurrencePattern, seriesEndDate) {
  if (!recurrencePattern) return 'Custom recurrence pattern';

  const { type, interval = 1 } = recurrencePattern;
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const positionNames = ['', 'First', 'Second', 'Third', 'Fourth', 'Fifth'];

  let description = '';

  switch (type) {
    case 'weekly':
      // Check if all 7 days are selected (Daily schedule)
      if (recurrencePattern.daysOfWeek?.length === 7 && interval === 1) {
        description = 'Daily';
      }
      // For true weekly (single day, any interval)
      else if (recurrencePattern.daysOfWeek?.length === 1) {
        const dayName = dayNames[recurrencePattern.daysOfWeek[0]];
        if (interval === 1) {
          description = `Weekly on ${dayName}`;
        } else if (interval === 2) {
          description = `Biweekly on ${dayName}`;
        } else {
          description = `Every ${interval} weeks on ${dayName}`;
        }
      }
      // For multiple days but not all 7 - show the specific days
      else if (recurrencePattern.daysOfWeek && recurrencePattern.daysOfWeek.length > 1) {
        const selectedDays = recurrencePattern.daysOfWeek
          .sort((a, b) => a - b)
          .map(day => dayNames[day]);

        if (selectedDays.length === 2) {
          description = `${selectedDays.join(' and ')}`;
        } else if (selectedDays.length > 2) {
          const lastDay = selectedDays.pop();
          description = `${selectedDays.join(', ')}, and ${lastDay}`;
        }
      }
      // Fallback
      else {
        description = interval === 1 ? 'Weekly' : `Every ${interval} weeks`;
      }
      break;

    case 'monthly':
      if (recurrencePattern.monthlyType === 'weekday') {
        const position = positionNames[recurrencePattern.weekdayPosition];
        const day = dayNames[recurrencePattern.weekday];
        if (interval === 1) {
          description = `${position} ${day} of every month`;
        } else {
          description = `${position} ${day} of every ${interval} months`;
        }
      } else {
        const day = recurrencePattern.dayOfMonth;
        const suffix = day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th';
        if (interval === 1) {
          description = `${day}${suffix} of every month`;
        } else {
          description = `${day}${suffix} of every ${interval} months`;
        }
      }
      break;

    case 'yearly':
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      const yearDay = recurrencePattern.dayOfMonth;
      const yearMonth = monthNames[recurrencePattern.month];
      const yearSuffix = yearDay === 1 ? 'st' : yearDay === 2 ? 'nd' : yearDay === 3 ? 'rd' : 'th';
      if (interval === 1) {
        description = `Every year on ${yearMonth} ${yearDay}${yearSuffix}`;
      } else {
        description = `Every ${interval} years on ${yearMonth} ${yearDay}${yearSuffix}`;
      }
      break;

    case 'custom':
      description = interval === 1 ? 'Weekly' : `Every ${interval} weeks`;
      break;

    default:
      description = 'Custom recurrence pattern';
  }

  // Add "(Indefinite)" suffix if no end date
  return !seriesEndDate ? `${description} (Indefinite)` : description;
}

// Generate time slots from startHour to endHour (hourly only)
function generateTimeSlots(startHour = 0, endHour = 23) {
  const slots = [];
  for (let hour = startHour; hour <= endHour; hour++) {
    const timeString = `${hour.toString().padStart(2, '0')}:00`;
    const displayTime = formatTime12Hour(timeString);
    slots.push({ timeString, displayTime, hour, minute: 0 });
  }
  return slots;
}

// Calculate top position for a schedule based on start time
function calculateSchedulePosition(startTime, slotHeight, startHour = 0) {
  if (!startTime) return 0;
  const [hours, minutes] = startTime.split(':').map(Number);
  const totalMinutes = (hours - startHour) * 60 + minutes;
  const position = (totalMinutes / 60) * slotHeight; // Hourly intervals
  // Clamp to visible range (0 to grid height)
  return Math.max(0, position);
}

// Calculate height for a schedule based on duration
function calculateScheduleHeight(durationSeconds, slotHeight) {
  if (!durationSeconds) return slotHeight; // Default to one hour
  const durationMinutes = durationSeconds / 60;
  const height = (durationMinutes / 60) * slotHeight; // Hourly intervals
  return Math.max(height, 30); // Minimum 30px for text visibility
}

// Detect and handle overlapping schedules
function layoutSchedules(schedules, slotHeight, startHour, totalHeight) {
  if (!schedules || schedules.length === 0) return [];
  
  // Calculate positions for all schedules
  const positioned = schedules.map(schedule => {
    const top = calculateSchedulePosition(schedule.startTime, slotHeight, startHour);
    const height = calculateScheduleHeight(schedule.video?.duration, slotHeight);
    const endTime = schedule.startTime ? (() => {
      const [hours, minutes] = schedule.startTime.split(':').map(Number);
      const durationMinutes = schedule.video?.duration ? schedule.video.duration / 60 : 60;
      const endMinutes = hours * 60 + minutes + durationMinutes;
      return endMinutes;
    })() : 0;
    
    return {
      ...schedule,
      top,
      height,
      endTime,
      left: 0,
      width: 1
    };
  }).filter(s => s.top < totalHeight && s.top + s.height > 0);

  // Check for schedules immediately below and ensure minimum height for 2 lines of text
  const lineHeight = 13; // 10px font * 1.3 line-height
  const minHeightForTwoLines = lineHeight * 2 + 8; // 2 lines + padding
  
  positioned.forEach((schedule, index) => {
    // Find if there's a schedule immediately below this one
    const scheduleBelow = positioned.find(s => 
      s.top > schedule.top && 
      s.top < schedule.top + schedule.height + 5 // Within 5px below
    );
    
    // If no schedule below and height is less than needed for 2 lines, increase it
    if (!scheduleBelow && schedule.height < minHeightForTwoLines) {
      schedule.height = Math.min(minHeightForTwoLines, totalHeight - schedule.top);
    }
  });

  // Sort by start time
  positioned.sort((a, b) => {
    if (a.top !== b.top) return a.top - b.top;
    return (a.endTime || 0) - (b.endTime || 0);
  });

  // Group overlapping schedules
  const columns = [];
  positioned.forEach(schedule => {
    let placed = false;
    for (let col of columns) {
      const lastInCol = col[col.length - 1];
      if (!lastInCol || lastInCol.top + lastInCol.height <= schedule.top) {
        col.push(schedule);
        placed = true;
        break;
      }
    }
    if (!placed) {
      columns.push([schedule]);
    }
  });

  // Calculate left position and width for each schedule
  const numColumns = columns.length;
  positioned.forEach(schedule => {
    const colIndex = columns.findIndex(col => col.includes(schedule));
    schedule.left = colIndex / numColumns;
    schedule.width = 1 / numColumns;
  });

  return positioned;
}

// Calculate which time slots have events and return dynamic heights based on actual event needs
function calculateDynamicSlotHeights(weekDays, getSchedulesForDate, timeSlots, startHour, baseSlotHeight) {
  const emptySlotHeight = 20; // Compressed height for empty slots
  const minEventHeight = 56; // Minimum height per event (for 2 lines of text)
  const fixedCardHeight = 52; // Fixed height for all cards - enough for 2 lines + border
  const cardGap = 4; // Gap between stacked cards

  // Track events per slot per day to calculate needed height
  const eventsPerSlotPerDay = new Map();

  weekDays.forEach(day => {
    const daySchedules = getSchedulesForDate(day);
    daySchedules.forEach(schedule => {
      if (schedule.startTime) {
        const [hours] = schedule.startTime.split(':').map(Number);
        const slotIndex = hours - startHour;
        if (slotIndex >= 0 && slotIndex < timeSlots.length) {
          if (!eventsPerSlotPerDay.has(slotIndex)) {
            eventsPerSlotPerDay.set(slotIndex, []);
          }
          eventsPerSlotPerDay.get(slotIndex).push({ schedule, day });
        }
      }
    });
  });

  // Calculate heights based on stacking logic (same as rendering logic)
  const slotHeights = timeSlots.map((_, slotIndex) => {
    const slotEvents = eventsPerSlotPerDay.get(slotIndex) || [];
    if (slotEvents.length === 0) {
      return emptySlotHeight;
    }

    // Group events by day to calculate stacking per day
    const eventsByDay = new Map();
    slotEvents.forEach(({ schedule, day }) => {
      const dayKey = day.toISOString();
      if (!eventsByDay.has(dayKey)) {
        eventsByDay.set(dayKey, []);
      }
      eventsByDay.get(dayKey).push(schedule);
    });

    // Find the maximum stacked height across all days for this slot
    let maxSlotBottom = 0;

    eventsByDay.forEach((daySchedules) => {
      // Sort schedules by start time
      const sortedSchedules = daySchedules.sort((a, b) => {
        const timeA = a.startTime || '00:00:00';
        const timeB = b.startTime || '00:00:00';
        return timeA.localeCompare(timeB);
      });

      // Calculate stacked positions for this day (same logic as rendering)
      const stackedPositions = [];
      sortedSchedules.forEach((schedule) => {
        const [hours, minutes] = schedule.startTime.split(':').map(Number);
        const minutesInHour = minutes;

        // Position within the hour (proportional to time)
        const positionInSlot = (minutesInHour / 60) * baseSlotHeight;

        // Find overlapping events already positioned and stack below them
        // Check for VISUAL overlap, not time overlap
        let cardTop = positionInSlot;
        stackedPositions.forEach((prev) => {
          // Check if this card would visually overlap with the previous card
          if (cardTop < prev.bottom) {
            // They would overlap visually - position this card below the previous one with a gap
            cardTop = Math.max(cardTop, prev.bottom + cardGap);
          }
        });

        const cardBottom = cardTop + fixedCardHeight;
        stackedPositions.push({
          top: cardTop,
          bottom: cardBottom
        });

        // Track the maximum bottom position for this slot
        maxSlotBottom = Math.max(maxSlotBottom, cardBottom);
      });
    });

    // Slot height must accommodate the bottom-most stacked card
    return Math.max(maxSlotBottom, minEventHeight + 10);
  });
  
  const cumulativePositions = [0];
  slotHeights.forEach((height, index) => {
    cumulativePositions.push(cumulativePositions[index] + height);
  });
  
  const totalHeight = cumulativePositions[cumulativePositions.length - 1];
  
  return { slotHeights, cumulativePositions, totalHeight, eventsPerSlotPerDay };
}

// Calculate position using dynamic slot heights
function calculatePositionWithDynamicSlots(startTime, slotHeights, cumulativePositions, startHour) {
  if (!startTime) return 0;
  const [hours, minutes] = startTime.split(':').map(Number);
  const slotIndex = hours - startHour;
  
  if (slotIndex < 0 || slotIndex >= cumulativePositions.length - 1) return 0;
  
  // Position within the slot based on minutes
  const slotHeight = slotHeights[slotIndex];
  const minutesOffset = (minutes / 60) * slotHeight;
  const position = cumulativePositions[slotIndex] + minutesOffset;
  
  return Math.max(0, position);
}

// Calculate height using dynamic slot heights (for events spanning multiple slots)
function calculateHeightWithDynamicSlots(startTime, durationSeconds, slotHeights, cumulativePositions, startHour, baseSlotHeight) {
  // Minimum height for 2 lines of text: fontSize (10px) * lineHeight (1.3) * 2 lines = 26px
  // Plus padding: 4px top + 4px bottom = 8px
  // Plus potential recurring badge: ~10px
  // Total minimum: ~52px to match customer portal
  const minHeightForTwoLines = 52;
  
  if (!startTime) return minHeightForTwoLines;
  
  const [startHours, startMinutes] = startTime.split(':').map(Number);
  const slotIndex = startHours - startHour;
  
  if (slotIndex < 0 || slotIndex >= slotHeights.length) {
    return minHeightForTwoLines;
  }
  
  // Use baseSlotHeight as reference for proportional calculations (consistent with slot height calculation)
  // If no duration, use minimum height
  if (!durationSeconds) return minHeightForTwoLines;
  
  // Calculate height based on duration using baseSlotHeight as reference
  const durationMinutes = durationSeconds / 60;
  const proportionalHeight = (durationMinutes / 60) * baseSlotHeight;
  
  // Ensure at least minimum height for 2 lines
  return Math.max(proportionalHeight, minHeightForTwoLines);
}

// Time-Grid Weekly View Component
function WeeklyView({ weekStart, selectedDate, schedulesByDate, onDateClick, onSlotClick, onEventClick, getSchedulesForDate }) {
  const weekDays = eachDayOfInterval({
    start: weekStart,
    end: endOfWeek(weekStart, { weekStartsOn: 0 })
  });

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const baseSlotHeight = 80; // Height of each hour slot in pixels when it has events
  const startHour = 0; // Start at 12 AM (midnight)
  const endHour = 23; // End at 11 PM (last slot goes to 11:59 PM)
  const timeSlots = generateTimeSlots(startHour, endHour);
  
  // Calculate dynamic slot heights based on events
  const { slotHeights, cumulativePositions, totalHeight } = 
    calculateDynamicSlotHeights(weekDays, getSchedulesForDate, timeSlots, startHour, baseSlotHeight);

  return (
    <div
      className="w-full rounded-xl overflow-hidden"
      style={{
        border: '1px solid var(--border-subtle)',
        background: 'var(--bg-surface)',
      }}
    >
      {/* Scrollable container with header inside */}
      <div className="relative" style={{ overflowY: 'auto', overflowX: 'hidden', maxHeight: '800px' }}>
        {/* Day Headers - Sticky at top, inside scrollable container */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '70px repeat(7, 1fr)',
            borderBottom: '1px solid var(--border-subtle)',
            background: 'linear-gradient(180deg, var(--bg-elevated) 0%, var(--bg-muted) 100%)',
            position: 'sticky',
            top: 0,
            zIndex: 30,
          }}
        >
          {/* Empty cell for time column */}
          <div style={{
            padding: 8,
            borderRight: '1px solid var(--border-subtle)',
          }}></div>
          {/* Day headers */}
          {dayNames.map((dayName, index) => {
            const day = weekDays[index];
            const isToday = isSameDay(day, new Date());
            const isSelected = isSameDay(day, selectedDate);
            const daySchedules = getSchedulesForDate(day);
            const hasClasses = daySchedules.length > 0;

            return (
              <div
                key={index}
                onClick={() => onDateClick(day)}
                style={{
                  textAlign: 'center',
                  padding: '10px 4px',
                  borderRight: index < 6 ? '1px solid var(--border-subtle)' : 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  background: isSelected
                    ? 'var(--primary-10)'
                    : isToday
                      ? 'var(--highlight-8)'
                      : 'transparent',
                }}
              >
                <div style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: isSelected ? 'var(--primary)' : 'var(--text-muted)',
                  marginBottom: 4,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}>{dayName}</div>
                <div style={{
                  width: 32,
                  height: 32,
                  margin: '0 auto',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 14,
                  fontWeight: 700,
                  background: isSelected
                    ? 'var(--primary)'
                    : isToday
                      ? 'var(--highlight-15)'
                      : 'transparent',
                  color: isSelected
                    ? 'white'
                    : isToday
                      ? 'var(--primary)'
                      : hasClasses
                        ? 'var(--text-primary)'
                        : 'var(--text-faint)',
                  border: isToday && !isSelected ? '2px solid var(--primary-30)' : 'none',
                }}>
                  {format(day, 'd')}
                </div>
              </div>
            );
          })}
        </div>

        {/* Time Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '70px repeat(7, 1fr)',
          minHeight: `${totalHeight}px`,
          background: 'var(--bg-surface)',
        }}>
          {/* Time Column - Premium styling */}
          <div style={{
            borderRight: '1px solid var(--border-subtle)',
            background: 'var(--bg-muted)',
            position: 'sticky',
            left: 0,
            zIndex: 10,
          }}>
            {timeSlots.map((slot, index) => {
              const height = slotHeights[index];
              return (
                <div
                  key={index}
                  style={{
                    height: `${height}px`,
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'var(--text-secondary)',
                    textAlign: 'right',
                    paddingRight: 8,
                    paddingTop: 4,
                    borderBottom: '1px solid var(--border-subtle)',
                  }}
                >
                  {slot.displayTime}
                </div>
              );
            })}
          </div>

          {/* Day Columns */}
          {weekDays.map((day, dayIndex) => {
            const isSelected = isSameDay(day, selectedDate);
            const daySchedules = getSchedulesForDate(day);
            
            // Sort schedules by time first to ensure consistent stacking
            const sortedDaySchedules = [...daySchedules].sort((a, b) => {
              const timeA = a.startTime || '00:00:00';
              const timeB = b.startTime || '00:00:00';
              return timeA.localeCompare(timeB);
            });

            // Calculate positions using dynamic slot heights - proportional to time like Google Calendar
            const positionedSchedules = sortedDaySchedules.map(schedule => {
              // Calculate position proportionally within the slot (like Google Calendar)
              const [hours, minutes] = schedule.startTime.split(':').map(Number);
              const slotIndex = hours - startHour;
              
              if (slotIndex < 0 || slotIndex >= cumulativePositions.length - 1) {
                return null;
              }
              
              const slotTop = cumulativePositions[slotIndex];
              const slotHeight = slotHeights[slotIndex];
              
              // IMPORTANT: Use baseSlotHeight for position calculation to maintain consistent time positioning
              // The slotHeight may be expanded for stacking, but we want time positions to be consistent
              // Position within slot is proportional to minutes (0-60 minutes maps to 0-baseSlotHeight)
              // This ensures 10:00 is at top, 10:30 is at middle, 10:59 is near bottom
              const minutesInHour = minutes;
              const positionInSlot = (minutesInHour / 60) * baseSlotHeight;
              const top = slotTop + positionInSlot;
              
              // Calculate height based on duration, using baseSlotHeight as reference for consistency
              const minHeightForTwoLines = 52;
              let height = minHeightForTwoLines;
              if (schedule.video?.duration) {
                const durationMinutes = schedule.video.duration / 60;
                // Calculate proportional height using baseSlotHeight (consistent reference)
                const proportionalHeight = (durationMinutes / 60) * baseSlotHeight;
                // Use proportional height if it's at least the minimum
                height = Math.max(proportionalHeight, minHeightForTwoLines);
              }
              
              const endTime = schedule.startTime ? (() => {
                const durationMinutes = schedule.video?.duration ? schedule.video.duration / 60 : 60;
                const endMinutes = hours * 60 + minutes + durationMinutes;
                return endMinutes;
              })() : 0;
              
              const slotBottom = cumulativePositions[slotIndex + 1];
              
              return {
                ...schedule,
                top,
                height,
                endTime,
                left: 0,
                width: 1,
                slotIndex,
                slotTop,
                slotBottom
              };
            }).filter(s => s !== null && s.top < totalHeight && s.top + s.height > 0);
            
            // Cards positioned at proportional time, but if they overlap, place them below each other
            const fixedCardHeight = 52; // Fixed height for cards - enough for 2 lines + border
            const cardGap = 4; // Gap between stacked cards to show the top line of the card below

            const adjustedSchedules = [];
            for (let i = 0; i < positionedSchedules.length; i++) {
              const schedule = positionedSchedules[i];
              let cardTop = schedule.top;

              // Check if this card overlaps VISUALLY with any previous card
              // We need to check visual overlap, not just time overlap, because cards have fixed height
              let maxBottom = cardTop;
              for (let j = 0; j < i; j++) {
                const prevSchedule = adjustedSchedules[j];

                // Only check cards in the same time slot (same hour)
                if (prevSchedule.slotIndex === schedule.slotIndex) {
                  const prevTop = prevSchedule.top;
                  const prevBottom = prevSchedule.top + prevSchedule.height;

                  // Check if this card would visually overlap with the previous card
                  // Visual overlap = new card's top position is within previous card's space
                  if (cardTop < prevBottom) {
                    // They would overlap visually - position this card below the previous one with a gap
                    maxBottom = Math.max(maxBottom, prevBottom + cardGap);
                  }
                }
              }
              
              adjustedSchedules.push({
                ...schedule,
                top: maxBottom,
                height: fixedCardHeight,
                left: 0,
                width: 1
              });
            }

            return (
              <div
                key={dayIndex}
                className="relative last:border-r-0"
                style={{
                  minHeight: `${totalHeight}px`,
                  borderRight: dayIndex < 6 ? '1px solid var(--border-subtle)' : 'none'
                }}
              >
                {/* Time slot grid lines */}
                {timeSlots.map((slot, slotIndex) => (
                  <div
                    key={slotIndex}
                    className="cursor-pointer transition-colors"
                    style={{
                      height: `${slotHeights[slotIndex]}px`,
                      borderBottom: '1px solid var(--border-subtle)',
                      boxSizing: 'border-box'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSlotClick(day, slot.hour);
                    }}
                  />
                ))}

                {/* Schedule Items */}
                {adjustedSchedules.map((schedule, scheduleIndex) => {
                  const timeDisplay = formatTime12Hour(schedule.startTime);
                  // Use the fixed card height - no adjustments needed
                  const displayHeight = schedule.height;
                  
                  // Only filter if card is completely out of bounds
                  if (schedule.top > totalHeight + 100) return null;

                  const isLiveClass = schedule.classType === 'live';
                  const title = isLiveClass ? schedule.liveTitle : (schedule.video?.title || schedule.archivedVideoTitle);

                  return (
                    <div
                      key={schedule.id}
                      className="absolute transition-all duration-200 cursor-pointer overflow-hidden"
                      style={{
                        top: `${schedule.top}px`,
                        left: `${schedule.left * 100}%`,
                        width: `calc(${schedule.width * 100}% - 6px)`,
                        height: `${displayHeight}px`,
                        marginLeft: '3px',
                        marginRight: '3px',
                        zIndex: 1,
                        borderRadius: 8,
                        backgroundColor: isLiveClass ? 'var(--primary-10)' : 'var(--secondary-8)',
                        border: '1px solid var(--border-subtle)',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEventClick(schedule);
                      }}
                      title={`${title || 'Scheduled'} at ${timeDisplay}`}
                    >
                      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: '4px 6px', gap: 2 }}>
                        {/* Row 1: TAG + TIME */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{
                            background: isLiveClass ? 'var(--primary)' : 'var(--secondary)',
                            color: 'white',
                            padding: '1px 4px',
                            borderRadius: 3,
                            fontSize: 7,
                            fontWeight: 700,
                            letterSpacing: '0.3px',
                            lineHeight: 1.2,
                          }}>{isLiveClass ? 'LIVE' : 'VIDEO'}</span>
                          {schedule.seriesId && <Repeat className="w-2 h-2" style={{ color: 'var(--recurring)' }} />}
                          <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-primary)' }}>{timeDisplay}</span>
                        </div>
                        {/* Row 2: TITLE */}
                        <div style={{
                          fontSize: 9,
                          fontWeight: 600,
                          lineHeight: 1.3,
                          color: 'var(--text-primary)',
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}>
                          {title || 'Scheduled'}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function SchedulePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('weekly'); // 'weekly' or 'monthly', default to weekly
  const [currentMonth, setCurrentMonth] = useState(() => new Date());
  const [currentWeek, setCurrentWeek] = useState(() => {
    const now = new Date();
    return startOfWeek(now, { weekStartsOn: 0 }); // Start of current week
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [showAttendeesModal, setShowAttendeesModal] = useState(false);
  const [selectedScheduleForAttendees, setSelectedScheduleForAttendees] = useState(null);
  const [selectedScheduleForModal, setSelectedScheduleForModal] = useState(null);
  const [showCheckInQR, setShowCheckInQR] = useState(false);
  const [preselectedVideoId, setPreselectedVideoId] = useState(null);
  const [preselectedTime, setPreselectedTime] = useState(null);
  const [isEditingTime, setIsEditingTime] = useState(false);
  const [editedTime, setEditedTime] = useState('');
  const [editedDate, setEditedDate] = useState('');
  const [originalDate, setOriginalDate] = useState(''); // Track original date to reset when switching scope
  // Edit mode recurrence state (for converting one-time to recurring)
  const [editRecurrenceType, setEditRecurrenceType] = useState('once');
  const [editDaysOfWeek, setEditDaysOfWeek] = useState([]);
  const [editWeeklyInterval, setEditWeeklyInterval] = useState(1);
  const [editMonthlyInterval, setEditMonthlyInterval] = useState(1);
  const [editMonthlyType, setEditMonthlyType] = useState('date');
  const [editYearlyInterval, setEditYearlyInterval] = useState(1);
  const [editEndType, setEditEndType] = useState('date');
  const [editEndDate, setEditEndDate] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState(null);
  const [showEditConfirm, setShowEditConfirm] = useState(false);
  const [showConvertToRecurringModal, setShowConvertToRecurringModal] = useState(false);
  const [pendingEdit, setPendingEdit] = useState(null);
  const [editScope, setEditScope] = useState('this'); // 'this', 'future', 'all'
  const [deleteScope, setDeleteScope] = useState('this'); // 'this', 'future', 'all'
  const queryClient = useQueryClient();
  const { studio, user } = useAuth();
  // Get studio ID - prefer studio object, fallback to user.studioId (same as customer portal)
  const studioId = studio?.id || user?.studioId;

  // Track when modal should open in edit mode
  const openInEditMode = useRef(false);


  // Initialize edited time and date when modal opens
  useEffect(() => {
    if (selectedScheduleForModal) {
      // Normalize time to HH:MM format (remove seconds if present)
      const normalizedTime = selectedScheduleForModal.startTime
        ? selectedScheduleForModal.startTime.substring(0, 5)
        : '';
      setEditedTime(normalizedTime);
      setEditedDate(selectedScheduleForModal.specificDate || '');
      setOriginalDate(selectedScheduleForModal.specificDate || ''); // Store original for scope reset

      // Initialize recurrence state for edit mode
      setEditRecurrenceType('once');
      // Default to all days selected when converting to recurring
      setEditDaysOfWeek([0, 1, 2, 3, 4, 5, 6]);
      setEditWeeklyInterval(1);
      setEditMonthlyInterval(1);
      setEditMonthlyType('date');
      setEditYearlyInterval(1);
      setEditEndType('date');
      // Default end date to 1 year from the schedule date
      const scheduleDate = selectedScheduleForModal.specificDate ? parseISO(selectedScheduleForModal.specificDate) : new Date();
      setEditEndDate(format(addYears(scheduleDate, 1), 'yyyy-MM-dd'));

      // Check if we should open in edit mode
      if (openInEditMode.current) {
        setIsEditingTime(true);
        openInEditMode.current = false;
      } else {
        setIsEditingTime(false);
      }
    }
  }, [selectedScheduleForModal]);

  // Force currentMonth/currentWeek to current period when studioId is available (same as customer portal)
  useEffect(() => {
    if (studioId) {
      const now = new Date();
      if (viewMode === 'weekly') {
        const currentWeekStart = startOfWeek(now, { weekStartsOn: 0 });
        if (format(currentWeek, 'yyyy-MM-dd') !== format(currentWeekStart, 'yyyy-MM-dd')) {
          setCurrentWeek(currentWeekStart);
        }
      } else {
        const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        if (format(currentMonth, 'yyyy-MM') !== format(currentMonthStart, 'yyyy-MM')) {
          setCurrentMonth(currentMonthStart);
        }
      }
    }
  }, [studioId, viewMode]); // Run when studioId or viewMode is available

  // Handle video selection from VideosPage
  useEffect(() => {
    if (location.state?.selectedVideoId) {
      setPreselectedVideoId(location.state.selectedVideoId);
      setShowAddModal(true);
      // Clear the state so it doesn't persist on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // Calculate date range based on view mode
  const calendarStart = viewMode === 'weekly' 
    ? startOfWeek(currentWeek, { weekStartsOn: 0 })
    : startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 0 });
  const calendarEnd = viewMode === 'weekly'
    ? endOfWeek(currentWeek, { weekStartsOn: 0 })
    : endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 0 });

  // Fetch schedules for the visible period
  const { data: schedules, isLoading } = useQuery({
    queryKey: ['schedules', studioId, viewMode === 'weekly' ? format(currentWeek, 'yyyy-MM-dd') : format(currentMonth, 'yyyy-MM')],
    queryFn: () => {
      // Fetch schedules for the month range
      const startDate = format(calendarStart, 'yyyy-MM-dd');
      const endDate = format(calendarEnd, 'yyyy-MM-dd');
      return schedulesAPI.getSchedules(studioId, { startDate, endDate });
    },
    enabled: !!studioId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    staleTime: 30000, // Consider data fresh for 30 seconds
    refetchInterval: false, // Disable auto-refresh to prevent rate limiting
  });

  // Get thumbnails for schedules (cached globally)
  const scheduleList = schedules?.data?.schedules || [];
  const videoIds = useMemo(() =>
    [...new Set(scheduleList.map(s => s.videoId).filter(Boolean))],
    [scheduleList]
  );
  const thumbnailsMap = useThumbnails(videoIds);

  const { data: videos } = useQuery({
    queryKey: ['videos-for-scheduling', 'local'],
    queryFn: () => videosAPI.getVideos({ source: 'local', limit: 1000 }), // Get all local videos
    retry: false,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    staleTime: 30000 // Consider stale after 30 seconds
  });

  const { data: categoriesData } = useQuery({
    queryKey: ['video-categories'],
    queryFn: () => videosAPI.getCategories(),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: Infinity
  });

  const deleteMutation = useMutation({
    onMutate: async (scheduleId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['schedules'] });

      // Snapshot all schedule queries
      const previousQueries = new Map();
      queryClient.getQueriesData({ queryKey: ['schedules'] }).forEach(([key, data]) => {
        previousQueries.set(JSON.stringify(key), data);
      });

      // Optimistically remove the schedule from all queries
      queryClient.getQueriesData({ queryKey: ['schedules'] }).forEach(([key, data]) => {
        if (data?.data?.schedules && Array.isArray(data.data.schedules)) {
          queryClient.setQueryData(key, {
            ...data,
            data: {
              ...data.data,
              schedules: data.data.schedules.filter(s => s.id !== scheduleId)
            }
          });
        }
      });

      return { previousQueries };
    },
    mutationFn: (id) => schedulesAPI.deleteSchedule(id),
    onSuccess: () => {
      // Refetch in background to get real data
      queryClient.refetchQueries({
        queryKey: ['schedules'],
        type: 'active'
      });
      queryClient.refetchQueries({
        queryKey: ['upcoming-schedules-countdown'],
        type: 'active'
      });
      toast.success('Schedule deleted successfully!');
    },
    onError: (error, scheduleId, context) => {
      // Rollback optimistic delete
      if (context?.previousQueries) {
        context.previousQueries.forEach((data, keyString) => {
          const key = JSON.parse(keyString);
          queryClient.setQueryData(key, data);
        });
      }
      toast.error(error.response?.data?.message || error.message || 'Failed to delete schedule');
    }
  });

  const deleteSeriesFromInstanceMutation = useMutation({
    mutationFn: (instanceId) => schedulesAPI.deleteSeriesFromInstance(instanceId),
    onSuccess: () => {
      // Refetch to get real data from server
      queryClient.refetchQueries({
        queryKey: ['schedules'],
        type: 'active'
      });
      queryClient.refetchQueries({
        queryKey: ['upcoming-schedules-countdown'],
        type: 'active'
      });
      toast.success('Recurring series deleted successfully!');
      setShowDeleteConfirm(false);
      setScheduleToDelete(null);
      setSelectedScheduleForModal(null);
      setDeleteScope('this'); // Reset to default
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || error.message || 'Failed to delete recurring series');
    }
  });

  const deleteSeriesMutation = useMutation({
    mutationFn: (seriesId) => schedulesAPI.deleteSeries(seriesId),
    onSuccess: () => {
      // Refetch to get real data from server
      queryClient.refetchQueries({
        queryKey: ['schedules'],
        type: 'active'
      });
      queryClient.refetchQueries({
        queryKey: ['upcoming-schedules-countdown'],
        type: 'active'
      });
      toast.success('Recurring series deleted successfully!');
      setShowDeleteConfirm(false);
      setScheduleToDelete(null);
      setSelectedScheduleForModal(null);
      setDeleteScope('this'); // Reset to default
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || error.message || 'Failed to delete recurring series');
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => schedulesAPI.updateSchedule(id, data),
    onMutate: async ({ id, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['schedules'] });

      // Snapshot all schedule queries for rollback
      const previousQueries = new Map();
      queryClient.getQueriesData({ queryKey: ['schedules'] }).forEach(([key, queryData]) => {
        previousQueries.set(JSON.stringify(key), queryData);
      });

      // Optimistically update the schedule in all queries
      queryClient.getQueriesData({ queryKey: ['schedules'] }).forEach(([key, queryData]) => {
        if (queryData?.data?.schedules && Array.isArray(queryData.data.schedules)) {
          queryClient.setQueryData(key, {
            ...queryData,
            data: {
              ...queryData.data,
              schedules: queryData.data.schedules.map(s =>
                s.id === id ? { ...s, ...data, startTime: data.startTime || s.startTime, specificDate: data.specificDate || s.specificDate } : s
              )
            }
          });
        }
      });

      // Close modal immediately for instant feel
      setIsEditingTime(false);
      setSelectedScheduleForModal(null);

      return { previousQueries };
    },
    onSuccess: () => {
      toast.success('Schedule updated successfully!');
    },
    onError: (error, variables, context) => {
      console.error('Error updating schedule:', error);
      // Rollback on error
      if (context?.previousQueries) {
        context.previousQueries.forEach((data, key) => {
          queryClient.setQueryData(JSON.parse(key), data);
        });
      }
      toast.error(error.response?.data?.message || 'Failed to update schedule');
    },
    onSettled: () => {
      // Always refetch for eventual consistency
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      queryClient.invalidateQueries({ queryKey: ['upcoming-schedules-countdown'] });
    }
  });

  // Update series from instance (Edit this and future)
  const updateSeriesFromInstanceMutation = useMutation({
    mutationFn: ({ instanceId, data }) => schedulesAPI.updateSeriesFromInstance(instanceId, data),
    onSuccess: () => {
      // Refetch to get real data from server
      queryClient.refetchQueries({
        queryKey: ['schedules'],
        type: 'active'
      });
      queryClient.refetchQueries({
        queryKey: ['upcoming-schedules-countdown'],
        type: 'active'
      });
      toast.success('Recurring series updated successfully (this and future events)!');
      setIsEditingTime(false);
      setSelectedScheduleForModal(null);
    },
    onError: (error) => {
      console.error('Error updating series from instance:', error);
      toast.error(error.response?.data?.message || 'Failed to update recurring series');
    }
  });

  // Update entire series (Edit all)
  const updateSeriesMutation = useMutation({
    mutationFn: ({ seriesId, data }) => schedulesAPI.updateSeries(seriesId, data),
    onSuccess: () => {
      // Refetch to get real data from server
      queryClient.refetchQueries({
        queryKey: ['schedules'],
        type: 'active'
      });
      queryClient.refetchQueries({
        queryKey: ['upcoming-schedules-countdown'],
        type: 'active'
      });
      toast.success('All events in recurring series updated successfully!');
      setIsEditingTime(false);
      setSelectedScheduleForModal(null);
    },
    onError: (error) => {
      console.error('Error updating series:', error);
      toast.error(error.response?.data?.message || 'Failed to update recurring series');
    }
  });

  // Convert one-time schedule to recurring series mutation
  const convertToRecurringMutation = useMutation({
    mutationFn: ({ scheduleId, recurrenceData }) =>
      schedulesAPI.convertToRecurring(scheduleId, recurrenceData),
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: ['schedules'],
        refetchType: 'active'
      });
      toast.success(`Converted to recurring series! Created ${response?.data?.newInstanceCount || 0} future instances.`);
      // Close edit mode and reset state
      setIsEditingTime(false);
      setSelectedScheduleForModal(null);
      setEditRecurrenceType('once');
      setEditEndDate('');
    },
    onError: (error) => {
      console.error('Error converting to recurring:', error);
      const message = error.response?.data?.message || error.message || 'Failed to convert to recurring';
      toast.error(message);
    }
  });

  // Organize schedules by date
  const schedulesByDate = useMemo(() => {
    const result = {};
    const allSchedules = schedules?.data?.schedules || [];

    // Get all days in the visible calendar range
    const visibleDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

    allSchedules.forEach(schedule => {
      // Handle schedules with specificDate or instanceDate
      if (schedule.specificDate || schedule.instanceDate) {
        const dateKey = format(parseISO(schedule.specificDate || schedule.instanceDate), 'yyyy-MM-dd');
        if (!result[dateKey]) {
          result[dateKey] = [];
        }
        result[dateKey].push(schedule);
      }
      // Also handle old-style recurring schedules with dayOfWeek
      else if (schedule.dayOfWeek !== null && schedule.dayOfWeek !== undefined) {
        // Recurring weekly schedule - show on all matching days of week
        visibleDays.forEach(day => {
          if (day.getDay() === schedule.dayOfWeek) {
            const dateKey = format(day, 'yyyy-MM-dd');
            if (!result[dateKey]) {
              result[dateKey] = [];
            }
            result[dateKey].push(schedule);
          }
        });
      }
    });

    // Sort schedules by time within each date
    Object.keys(result).forEach(dateKey => {
      result[dateKey].sort((a, b) => {
        const timeA = a.startTime || '00:00:00';
        const timeB = b.startTime || '00:00:00';
        return timeA.localeCompare(timeB);
      });
    });

    return result;
  }, [schedules, calendarStart, calendarEnd]);

  // Get schedules for a specific date
  const getSchedulesForDate = (date) => {
    const dateKey = format(date, 'yyyy-MM-dd');
    return schedulesByDate[dateKey] || [];
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    // Only set the date, don't open modal - user can see videos for that day
  };

  const handleSlotClick = (date, hour = null) => {
    setSelectedDate(date);
    if (hour !== null) {
      // Format hour as HH:00 for the time input
      setPreselectedTime(`${hour.toString().padStart(2, '0')}:00`);
    } else {
      setPreselectedTime(null);
    }
    setShowAddModal(true);
  };

  // Handle edit scope change - reset date to original when switching to 'future' or 'all'
  const handleEditScopeChange = (newScope) => {
    setEditScope(newScope);
    // When switching to 'future' or 'all', reset date to original
    // (date changes are ignored for series edits, so prevent confusion)
    if (newScope !== 'this') {
      setEditedDate(originalDate);
    }
  };

  const handleSaveTime = () => {
    if (!selectedScheduleForModal || !editedTime || !editedDate) return;

    // DELETED VIDEO CHECK - Block moving deleted video schedules to future (using local time)
    // Only consider deleted if: videoDeleted flag is true, OR (videoId is null AND we have archived title)
    const isVideoClass = selectedScheduleForModal.classType !== 'live';
    const hasDeletedVideoFlag = selectedScheduleForModal.videoDeleted === true;
    const hasArchivedVideoInfo = selectedScheduleForModal.archivedVideoTitle != null;
    const videoIdIsNull = selectedScheduleForModal.videoId === null || selectedScheduleForModal.videoId === undefined;
    const scheduleHasDeletedVideo = isVideoClass && (hasDeletedVideoFlag || (videoIdIsNull && hasArchivedVideoInfo));

    if (scheduleHasDeletedVideo) {
      // Parse target date and time in local timezone
      const [year, month, day] = editedDate.split('-').map(Number);
      const [hours, minutes] = editedTime.split(':').map(Number);
      const targetDateTime = new Date(year, month - 1, day, hours, minutes, 0, 0);
      const now = new Date();

      if (targetDateTime > now) {
        const videoTitle = selectedScheduleForModal.archivedVideoTitle || selectedScheduleForModal.video?.title || 'Unknown Video';
        toast.error(`Cannot move this schedule to a future time - the video "${videoTitle}" has been deleted. Please assign a new video or delete this schedule.`, { duration: 6000 });
        return;
      }
    }

    // Check if converting to recurring
    const isConvertingToRecurring = editRecurrenceType !== 'once' &&
      !selectedScheduleForModal.seriesId && !selectedScheduleForModal.series_id;

    // Validate recurrence data if converting
    if (isConvertingToRecurring) {
      if (editRecurrenceType === 'daily' && editDaysOfWeek.length === 0) {
        toast.error('Please select at least one day of the week');
        return;
      }
      if (editRecurrenceType === 'weekly' && editDaysOfWeek.length === 0) {
        toast.error('Please select at least one day of the week');
        return;
      }
      if (editEndType === 'date' && !editEndDate) {
        toast.error('Please select an end date for the recurring series');
        return;
      }
    }

    // Store the pending edit with recurrence info
    setPendingEdit({
      id: selectedScheduleForModal.id,
      startTime: editedTime,
      specificDate: editedDate,
      isConvertingToRecurring,
      recurrenceType: editRecurrenceType,
      daysOfWeek: editDaysOfWeek,
      weeklyInterval: editWeeklyInterval,
      monthlyInterval: editMonthlyInterval,
      monthlyType: editMonthlyType,
      yearlyInterval: editYearlyInterval,
      endType: editEndType,
      endDate: editEndDate
    });
    setShowEditConfirm(true);
  };

  // UNIFIED conflict checking - used for BOTH creating AND editing schedules
  // This ensures ALL schedule operations use the same conflict detection ecosystem
  //
  // Parameters (all optional - falls back to formData if not provided):
  //   overrideStartTime, overrideSpecificDate - for edit mode
  //   overrideDuration - duration in seconds (for edit mode)
  //   excludeScheduleId - exclude this schedule from check (for edit mode)
  //   scheduleToCheck - the schedule being edited (to get its duration)
  const checkForConflicts = (options = {}) => {
    const {
      overrideStartTime,
      overrideSpecificDate,
      overrideDuration,
      excludeScheduleId,
      scheduleToCheck
    } = options;

    const startTime = overrideStartTime || formData.startTime;
    const specificDate = overrideSpecificDate || formData.specificDate;

    if (!startTime || !specificDate) {
      return null;
    }

    // Determine duration based on class type
    let newClassDuration = 0; // Duration in seconds

    if (overrideDuration !== undefined) {
      // Use provided duration (for edits)
      newClassDuration = overrideDuration;
    } else if (scheduleToCheck) {
      // Get duration from schedule being edited
      const isLive = scheduleToCheck.classType === 'live';
      if (isLive) {
        newClassDuration = scheduleToCheck.liveDuration || 3600;
      } else {
        newClassDuration = scheduleToCheck.video?.duration || 0;
      }
    } else if (classType === 'live') {
      // New live class - use liveDuration (already in seconds)
      newClassDuration = formData.liveDuration || 3600; // Default 1 hour
    } else {
      // New video class - need videoId and video duration
      if (!formData.videoId) return null;
      const selectedVideo = videos?.find(v => v.id === formData.videoId);
      if (!selectedVideo) return null;
      newClassDuration = selectedVideo.duration || 0;
    }

    // Convert new schedule start time to minutes since midnight
    const [newHours, newMinutes] = startTime.split(':').map(Number);
    const newStartMinutes = newHours * 60 + newMinutes;
    const newEndMinutes = newStartMinutes + Math.ceil(newClassDuration / 60);

    // Calculate dates for checking
    const currentDate = parseISO(specificDate);
    const currentDateStr = format(currentDate, 'yyyy-MM-dd');

    // Calculate previous day (to check for classes extending past midnight into current day)
    const previousDay = new Date(currentDate);
    previousDay.setDate(previousDay.getDate() - 1);
    const previousDayStr = format(previousDay, 'yyyy-MM-dd');

    // Calculate next day (to check if new schedule extends into next day)
    const nextDay = new Date(currentDate);
    nextDay.setDate(nextDay.getDate() + 1);
    const nextDayStr = format(nextDay, 'yyyy-MM-dd');

    // Get schedules for current date, previous date, and next date
    // IMPORTANT: Exclude the schedule being edited (if provided)
    // Use 'schedules' from main component (not 'existingSchedules' which is in AddScheduleModal)
    const schedulesToCheck = schedules?.data?.schedules?.filter(schedule => {
      // Exclude the schedule being edited
      if (excludeScheduleId && schedule.id === excludeScheduleId) {
        return false;
      }
      if (!schedule.specificDate) return false;
      const scheduleDate = format(parseISO(schedule.specificDate), 'yyyy-MM-dd');
      return scheduleDate === currentDateStr || scheduleDate === previousDayStr || scheduleDate === nextDayStr;
    }) || [];

    // Check each existing schedule for overlap
    for (const schedule of schedulesToCheck) {
      // Handle both video and live classes for existing schedule duration
      const isExistingLiveClass = schedule.classType === 'live';
      const existingDuration = isExistingLiveClass
        ? (schedule.liveDuration || 3600) // Live class default 1 hour
        : (schedule.video?.duration || 0);

      const [existingHours, existingMinutes] = (schedule.startTime || '00:00:00').split(':').map(Number);
      const existingStartMinutes = existingHours * 60 + existingMinutes;
      const existingEndMinutes = existingStartMinutes + Math.ceil(existingDuration / 60);

      const scheduleDate = format(parseISO(schedule.specificDate), 'yyyy-MM-dd');

      // Get title for conflict message (handle both video and live classes)
      const conflictTitle = isExistingLiveClass
        ? (schedule.liveTitle || 'Live class')
        : (schedule.video?.title || schedule.archivedVideoTitle || 'Another class');

      if (scheduleDate === previousDayStr) {
        // Schedule from previous day - only conflicts if it extends past midnight
        if (existingEndMinutes > 1440) {
          const extendIntoCurrentDay = existingEndMinutes - 1440;
          // Check if new schedule starts before previous-day schedule ends
          if (newStartMinutes < extendIntoCurrentDay) {
            return {
              conflictingSchedule: schedule,
              conflictTitle,
              existingStart: `${String(existingHours).padStart(2, '0')}:${String(existingMinutes).padStart(2, '0')} (previous day)`,
              existingEnd: `${String(Math.floor(extendIntoCurrentDay / 60)).padStart(2, '0')}:${String(extendIntoCurrentDay % 60).padStart(2, '0')} (current day)`
            };
          }
        }
      } else if (scheduleDate === nextDayStr) {
        // Schedule on next day - only conflicts if new schedule extends past midnight
        if (newEndMinutes > 1440) {
          const newExtendIntoNextDay = newEndMinutes - 1440;
          // Check if existing schedule starts before new schedule ends in next day
          if (existingStartMinutes < newExtendIntoNextDay) {
            return {
              conflictingSchedule: schedule,
              conflictTitle,
              existingStart: `${String(existingHours).padStart(2, '0')}:${String(existingMinutes).padStart(2, '0')} (next day)`,
              existingEnd: `${String(Math.floor(existingEndMinutes / 60)).padStart(2, '0')}:${String(existingEndMinutes % 60).padStart(2, '0')}`
            };
          }
        }
      } else {
        // Schedule on current day - normal overlap check
        if (newStartMinutes < existingEndMinutes && newEndMinutes > existingStartMinutes) {
          return {
            conflictingSchedule: schedule,
            conflictTitle,
            existingStart: `${String(existingHours).padStart(2, '0')}:${String(existingMinutes).padStart(2, '0')}`,
            existingEnd: `${String(Math.floor(existingEndMinutes / 60)).padStart(2, '0')}:${String(existingEndMinutes % 60).padStart(2, '0')}`
          };
        }
      }
    }

    return null;
  };

  const confirmEdit = () => {
    if (!pendingEdit) return;

    const schedule = selectedScheduleForModal;

    // Handle converting to recurring
    if (pendingEdit.isConvertingToRecurring) {
      // Check if schedule has a valid ID
      if (!schedule.id || schedule.id.startsWith('temp-')) {
        toast.error('This schedule hasn\'t been synced yet. Please wait a moment and try again.', { duration: 4000 });
        setShowEditConfirm(false);
        setPendingEdit(null);
        return;
      }

      // Build recurrence pattern based on type (must match create flow logic)
      let recurrencePattern = {};
      const scheduleDate = parseISO(pendingEdit.specificDate);

      if (pendingEdit.recurrenceType === 'daily') {
        // Daily with selected days - use weekly pattern with interval 1
        recurrencePattern = {
          type: 'weekly',
          daysOfWeek: pendingEdit.daysOfWeek,
          interval: 1
        };
      } else if (pendingEdit.recurrenceType === 'weekly') {
        // Weekly repeats on the same day of week as the start date
        recurrencePattern = {
          type: 'weekly',
          daysOfWeek: [scheduleDate.getDay()],
          interval: pendingEdit.weeklyInterval
        };
      } else if (pendingEdit.recurrenceType === 'monthly') {
        if (pendingEdit.monthlyType === 'date') {
          recurrencePattern = {
            type: 'monthly',
            monthlyType: 'date',
            dayOfMonth: scheduleDate.getDate(),
            interval: pendingEdit.monthlyInterval
          };
        } else {
          const weekOfMonth = Math.ceil(scheduleDate.getDate() / 7);
          recurrencePattern = {
            type: 'monthly',
            monthlyType: 'weekday',
            weekdayPosition: weekOfMonth,
            weekday: scheduleDate.getDay(),
            interval: pendingEdit.monthlyInterval
          };
        }
      }

      // Include time/date changes if they differ from the original
      const originalTime = schedule.startTime?.substring(0, 5) || '';
      const originalDate = schedule.specificDate;
      const recurrenceData = {
        recurrencePattern,
        seriesEndDate: pendingEdit.endType === 'never' ? null : pendingEdit.endDate
      };

      // Include updated time/date if changed
      if (pendingEdit.startTime !== originalTime) {
        recurrenceData.startTime = pendingEdit.startTime;
      }
      if (pendingEdit.specificDate !== originalDate) {
        recurrenceData.specificDate = pendingEdit.specificDate;
      }

      convertToRecurringMutation.mutate({
        scheduleId: schedule.id,
        recurrenceData
      });

      setShowEditConfirm(false);
      setPendingEdit(null);
      return;
    }

    const updateData = {
      startTime: pendingEdit.startTime,
      specificDate: pendingEdit.specificDate
    };

    // Check for conflicts BEFORE updating (using unified conflict checker)
    const conflict = checkForConflicts({
      overrideStartTime: pendingEdit.startTime,
      overrideSpecificDate: pendingEdit.specificDate,
      excludeScheduleId: schedule.id,
      scheduleToCheck: schedule
    });

    if (conflict) {
      const title = conflict.conflictTitle || conflict.conflictingSchedule?.video?.title || conflict.conflictingSchedule?.liveTitle || 'Another class';
      toast.error(
        `Scheduling conflict! ${title} is scheduled from ${conflict.existingStart} to ${conflict.existingEnd}. Please choose a different time.`,
        { duration: 5000 }
      );
      return;
    }

    // Determine which API to call based on edit scope
    if (schedule?.seriesId && editScope !== 'this') {
      // Recurring schedule - edit future or all
      if (editScope === 'future') {
        // Edit this and future events
        updateSeriesFromInstanceMutation.mutate({
          instanceId: pendingEdit.id,
          data: updateData
        });
      } else if (editScope === 'all') {
        // Edit all events in series
        updateSeriesMutation.mutate({
          seriesId: schedule.seriesId,
          data: updateData
        });
      }
    } else {
      // Edit this event only (or non-recurring schedule)
      updateMutation.mutate({
        id: pendingEdit.id,
        data: updateData
      });
    }

    setShowEditConfirm(false);
    setPendingEdit(null);
    setIsEditingTime(false);
    setEditScope('this'); // Reset to default
  };

  const confirmDelete = () => {
    if (!scheduleToDelete) return;

    const schedule = scheduleToDelete;

    // Determine which API to call based on delete scope
    if (schedule?.seriesId && deleteScope !== 'this') {
      // Recurring schedule - delete future or all
      if (deleteScope === 'future') {
        // Delete this and future events
        deleteSeriesFromInstanceMutation.mutate(schedule.id);
      } else if (deleteScope === 'all') {
        // Delete all events in series
        deleteSeriesMutation.mutate(schedule.seriesId);
      }
    } else {
      // Delete this event only (or non-recurring schedule)
      deleteMutation.mutate(schedule.id);
      setShowDeleteConfirm(false);
      setScheduleToDelete(null);
      setSelectedScheduleForModal(null);
      setDeleteScope('this'); // Reset to default
    }
  };

  const handleMonthChange = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = direction === 'next' ? addMonths(prev, 1) : subMonths(prev, 1);
      // Force calendar to update to this month
      return newMonth;
    });
  };

  const handleWeekChange = (direction) => {
    setCurrentWeek(prev => {
      const newWeek = direction === 'next' ? addWeeks(prev, 1) : subWeeks(prev, 1);
      // Invalidate queries when week changes to refetch schedules
      queryClient.invalidateQueries(['schedules', studioId]);
      return newWeek;
    });
  };

  // Custom tile content for calendar
  const tileContent = ({ date, view }) => {
    if (view === 'month') {
      const daySchedules = getSchedulesForDate(date);
      if (daySchedules.length > 0) {
        return (
          <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '2px', justifyContent: 'center' }}>
            {daySchedules.slice(0, 3).map((schedule, idx) => {
              const isLive = schedule.classType === 'live';
              const title = isLive ? schedule.liveTitle : (schedule.video?.title || schedule.archivedVideoTitle);
              return (
                <div
                  key={idx}
                  style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    backgroundColor: isLive ? 'var(--primary)' : 'var(--secondary)'
                  }}
                  title={`${title || 'Scheduled'} at ${formatTime12Hour(schedule.startTime)}`}
                />
              );
            })}
            {daySchedules.length > 3 && (
              <div style={{ fontSize: '8px', color: '#7d5e3e', fontWeight: '600' }}>
                +{daySchedules.length - 3}
              </div>
            )}
          </div>
        );
      }
    }
    return null;
  };

  // Custom tile className for highlighting dates with schedules
  const tileClassName = ({ date, view }) => {
    if (view === 'month') {
      const classes = [];
      
      // Get the first day of the current month being viewed
      const viewingMonthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const viewingMonthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
      
      // Check if this date falls within the current month being viewed
      const isCurrentMonth = date >= viewingMonthStart && date <= viewingMonthEnd;
      const isToday = isSameDay(date, new Date());
      
      // Only gray out dates that are NOT in the current month being viewed
      // These are the "bleed" dates from previous/next months
      if (!isCurrentMonth) {
        classes.push('other-month');
        // Don't apply any other styling to dates from other months
        return classes.join(' ');
      }
      
      // All styling below only applies to dates in the current month
      
      // Only highlight today if it's in the current month being viewed
      if (isToday) {
        classes.push('today-in-month');
      }
      
      const daySchedules = getSchedulesForDate(date);
      if (daySchedules.length > 0) {
        classes.push('has-schedules');
      }
      if (isSameDay(date, selectedDate)) {
        classes.push('selected-date');
      }
      return classes.length > 0 ? classes.join(' ') : null;
    }
    return null;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-xl md:text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>Class Schedule</h1>
          <Link
            to="/settings?tab=scheduling"
            className="p-1.5 md:p-2 rounded-lg transition-all duration-300"
            style={{ color: 'var(--primary)' }}
            title="Schedule Settings"
          >
            <Settings className="w-4 h-4 md:w-5 md:h-5" />
          </Link>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setSelectedDate(new Date());
              setShowAddModal(true);
            }}
            className="flex items-center gap-1.5 text-xs md:text-base px-2.5 md:px-4 py-1.5 md:py-2 rounded-lg font-medium transition-all duration-300"
            style={{
              background: 'linear-gradient(135deg, var(--primary-hover), var(--primary))',
              color: 'var(--bg-base)',
              boxShadow: '0 4px 12px var(--primary-30)',
            }}
          >
            <Plus className="w-4 h-4" />
            Schedule Class
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Calendar View */}
        <div className="lg:col-span-4">
          <div
            className="p-3 md:p-4 rounded-xl transition-all duration-300"
            style={{
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-subtle)',
              maxHeight: isMobile ? 'none' : '880px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Calendar Navigation - All in one row */}
            <div className="flex items-center justify-between gap-2 mb-3 md:mb-4" style={{ flexShrink: 0 }}>
              {/* Left Chevron */}
              <button
                onClick={() => viewMode === 'weekly' ? handleWeekChange('prev') : handleMonthChange('prev')}
                className="p-2 rounded-lg transition-all duration-300"
                style={{ color: 'var(--text-secondary)' }}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              {/* Center Group: Date Range + Toggle + Today */}
              <div className="flex items-center gap-2 md:gap-3 flex-wrap justify-center">
                {/* Date Range */}
                <h2 className="text-base md:text-xl font-bold text-center" style={{ color: 'var(--text-primary)' }}>
                  {viewMode === 'weekly'
                    ? isMobile
                      ? `${format(calendarStart, 'MMM d')} - ${format(calendarEnd, 'd')}`
                      : `${format(calendarStart, 'MMM d')} - ${format(calendarEnd, 'MMM d, yyyy')}`
                    : format(currentMonth, 'MMMM yyyy')
                  }
                </h2>

                {/* View Mode Toggle */}
                <div
                  className="flex items-center gap-1 rounded-lg p-1"
                  style={{ background: 'var(--highlight-5)', border: '1px solid var(--border-subtle)' }}
                >
                  <button
                    onClick={() => {
                      setViewMode('weekly');
                      const today = new Date();
                      setCurrentWeek(startOfWeek(today, { weekStartsOn: 0 }));
                      queryClient.invalidateQueries(['schedules', studioId]);
                    }}
                    className="px-2 md:px-3 py-1 md:py-1.5 rounded-md text-xs md:text-sm font-medium transition-all duration-200"
                    style={viewMode === 'weekly' ? {
                      background: 'linear-gradient(135deg, var(--primary-hover), var(--primary))',
                      color: 'var(--bg-base)',
                      boxShadow: '0 2px 8px var(--primary-30)',
                    } : {
                      color: 'var(--text-secondary)',
                    }}
                  >
                    Weekly
                  </button>
                  <button
                    onClick={() => {
                      setViewMode('monthly');
                      const today = new Date();
                      setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
                      queryClient.invalidateQueries(['schedules', studioId]);
                    }}
                    className="px-2 md:px-3 py-1 md:py-1.5 rounded-md text-xs md:text-sm font-medium transition-all duration-200"
                    style={viewMode === 'monthly' ? {
                      background: 'linear-gradient(135deg, var(--primary-hover), var(--primary))',
                      color: 'var(--bg-base)',
                      boxShadow: '0 2px 8px var(--primary-30)',
                    } : {
                      color: 'var(--text-secondary)',
                    }}
                  >
                    Monthly
                  </button>
                </div>

                {/* Today Button */}
                <button
                  onClick={() => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    setSelectedDate(today);
                    if (viewMode === 'weekly') {
                      setCurrentWeek(startOfWeek(today, { weekStartsOn: 0 }));
                    } else {
                      setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
                    }
                    queryClient.invalidateQueries(['schedules', studioId]);
                  }}
                  className="flex items-center gap-1.5 px-2.5 md:px-3 py-1 md:py-1.5 rounded-lg text-xs md:text-sm font-medium transition-all duration-300"
                  style={{
                    background: 'var(--primary-10)',
                    border: '1px solid var(--primary-20)',
                    color: 'var(--primary)',
                  }}
                >
                  <CalendarIcon className="w-3.5 h-3.5 md:w-4 md:h-4" />
                  <span className="hidden sm:inline">Today</span>
                </button>
              </div>

              {/* Right Chevron */}
              <button
                onClick={() => viewMode === 'weekly' ? handleWeekChange('next') : handleMonthChange('next')}
                className="p-2 rounded-lg transition-all duration-300"
                style={{ color: 'var(--text-secondary)' }}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            <div style={{ flex: 1, minHeight: 0 }} className={isMobile ? 'overflow-x-auto' : ''}>
              {viewMode === 'weekly' ? (
                <div className={isMobile ? 'min-w-[600px]' : ''}>
                  <WeeklyView
                    weekStart={calendarStart}
                    selectedDate={selectedDate}
                    schedulesByDate={schedulesByDate}
                    onDateClick={handleDateClick}
                    onSlotClick={handleSlotClick}
                    onEventClick={(schedule) => setSelectedScheduleForModal(schedule)}
                    getSchedulesForDate={getSchedulesForDate}
                  />
                </div>
              ) : (
                <Calendar
                  value={selectedDate}
                  activeStartDate={currentMonth}
                  onActiveStartDateChange={({ activeStartDate }) => {
                    if (activeStartDate) {
                      setCurrentMonth(activeStartDate);
                    }
                  }}
                  tileContent={tileContent}
                  tileClassName={tileClassName}
                  onClickDay={handleDateClick}
                  className="w-full border-0"
                  navigationLabel={null}
                  formatMonthYear={(locale, date) => ''}
                  selectRange={false}
                  showNeighboringMonth={true}
                />
              )}
            </div>

            {viewMode === 'monthly' && (
              <div className="mt-4 flex items-center gap-4 text-sm" style={{ color: 'var(--text-secondary)', flexShrink: 0, flexWrap: 'wrap' }}>
                <div className="flex items-center gap-2">
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: 'var(--secondary)' }}></div>
                  <span style={{ color: 'var(--secondary)' }}>Video Class</span>
                </div>
                <div className="flex items-center gap-2">
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: 'var(--primary)' }}></div>
                  <span style={{ color: 'var(--primary)' }}>Live Class</span>
                </div>
                <div className="flex items-center gap-2">
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#dbc9b9' }}></div>
                  <span>Selected</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Selected Date Details - "At A Glance" Sidebar */}
        <div className="lg:col-span-1" style={{ height: 'fit-content' }}>
          <div className="card hover:shadow-lg transition-all duration-300 border-2 border-transparent hover:border-[var(--border-subtle)] flex flex-col w-full lg:max-w-[280px] p-3" style={{ maxHeight: isMobile ? 'none' : '880px' }}>
            <div className="mb-3 pb-2 border-b flex-shrink-0">
              <h3 className="font-bold text-[var(--text-primary)] text-sm">
                At A Glance
              </h3>
              <p className="text-[var(--text-secondary)] text-xs">
                {format(selectedDate, 'EEEE, MMMM d')} • {getSchedulesForDate(selectedDate).length} {getSchedulesForDate(selectedDate).length === 1 ? 'class' : 'classes'}
              </p>
            </div>

            <div className={`flex-1 overflow-y-auto overflow-x-hidden ${isMobile ? 'flex flex-wrap gap-2' : 'space-y-2'}`}>
              {isLoading ? (
                <div className="text-center py-8 w-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600 mx-auto"></div>
                </div>
              ) : getSchedulesForDate(selectedDate).length > 0 ? (
                getSchedulesForDate(selectedDate).map((schedule) => {
                  const currentBookings = schedule.current_bookings || 0;
                  const maxCapacity = schedule.max_capacity || 10;
                  const availableSpots = maxCapacity - currentBookings;

                  return (
                    <div
                      key={schedule.id}
                      onClick={() => setSelectedScheduleForModal(schedule)}
                      className={`rounded-lg border border-[var(--border-subtle)] group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden cursor-pointer ${isMobile ? 'w-[calc(50%-4px)] min-w-[140px]' : ''}`}
                                      style={{ background: 'var(--bg-surface)' }}
                    >
                      {/* Compact thumbnail banner */}
                      <div className="relative h-16" style={{ background: schedule.classType === 'live' ? 'linear-gradient(to bottom right, var(--primary-10), var(--primary-20))' : 'linear-gradient(to bottom right, var(--secondary-10), var(--secondary-20))' }}>
                        {schedule.classType === 'live' ? (
                          <div className="w-full h-full flex items-center justify-center">
                            <Users className="w-6 h-6" style={{ color: 'var(--primary)' }} />
                          </div>
                        ) : thumbnailsMap[schedule.videoId] ? (
                          <img
                            src={thumbnailsMap[schedule.videoId]}
                            alt={schedule.video?.title || 'Video thumbnail'}
                            className="w-full h-full object-cover opacity-90"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Video className="w-6 h-6 text-[var(--text-muted)]" />
                          </div>
                        )}
                        {/* Category badge */}
                        {(schedule.classType === 'live' ? schedule.liveCategory : schedule.video?.category) && (
                          <div className="absolute top-1 right-1 text-xs font-semibold px-1.5 py-0.5 rounded-full shadow-lg flex items-center gap-0.5" style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border-subtle)' }}>
                            <span>{CATEGORIES.find(c => c.value === (schedule.classType === 'live' ? schedule.liveCategory : schedule.video?.category))?.icon}</span>
                            <span className="hidden sm:inline">{CATEGORIES.find(c => c.value === (schedule.classType === 'live' ? schedule.liveCategory : schedule.video?.category))?.label}</span>
                          </div>
                        )}
                        {/* Time badge overlay */}
                        <div className="absolute top-1 left-1 bg-primary-900 bg-opacity-90 text-white px-1.5 py-0.5 rounded text-xs font-semibold">
                          {formatTime12Hour(schedule.startTime)}
                        </div>
                        {/* Action buttons */}
                        <div className="absolute bottom-1 right-1 flex gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openInEditMode.current = true;
                              setSelectedScheduleForModal(schedule);
                            }}
                            className="bg-blue-600 bg-opacity-90 hover:bg-opacity-100 text-white p-1 rounded transition-all duration-300"
                            title="Edit schedule"
                          >
                            <Edit className="w-3 h-3" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setScheduleToDelete(schedule);
                              setShowDeleteConfirm(true);
                            }}
                            className="bg-red-600 bg-opacity-90 hover:bg-opacity-100 text-white p-1 rounded transition-all duration-300"
                            title="Delete schedule"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>

                      {/* Compact info section */}
                      <div className="p-2">
                        <h4 className="font-semibold text-[var(--text-primary)] text-sm line-clamp-1 mb-0.5">
                          {schedule.classType === 'live'
                            ? schedule.liveTitle
                            : (schedule.video?.title || schedule.archivedVideoTitle || 'Unknown Video')}
                        </h4>
                        <p className="text-[var(--text-secondary)] text-xs mb-1">
                          {schedule.classType === 'live'
                            ? schedule.liveInstructorName
                            : `${schedule.video?.instructor?.firstName || ''} ${schedule.video?.instructor?.lastName || ''}`.trim()}
                        </p>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {/* Video Deleted badge */}
                          {schedule.videoDeleted && (
                            <span style={{ backgroundColor: 'var(--highlight-10)', color: 'var(--highlight)', padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', border: '1px solid var(--highlight)' }}>
                              🗑️ Deleted
                            </span>
                          )}
                          {/* Live class badge */}
                          {schedule.classType === 'live' && (
                            <span style={{ backgroundColor: 'var(--primary-10)', color: 'var(--primary-dark)', padding: '2px 6px', borderRadius: '4px', fontSize: '12px', fontWeight: '600' }}>
                              LIVE
                            </span>
                          )}
                          <span className="bg-[var(--highlight-10)] text-[var(--text-secondary)] px-1.5 py-0.5 rounded text-xs">
                            {(() => {
                              const durationSeconds = schedule.classType === 'live'
                                ? (schedule.liveDuration || 0)
                                : (schedule.video?.duration || schedule.archivedVideoDuration || 0);
                              const minutes = Math.floor(durationSeconds / 60);
                              const seconds = durationSeconds % 60;
                              if (minutes > 0 && seconds > 0) {
                                return `${minutes}m ${seconds}s`;
                              } else if (minutes > 0) {
                                return `${minutes}m`;
                              } else if (seconds > 0) {
                                return `${seconds}s`;
                              }
                              return '0m';
                            })()}
                          </span>
                          {schedule.seriesId && (
                            <span className="px-1.5 py-0.5 rounded text-xs flex items-center gap-0.5" style={{ background: 'var(--recurring-10)', color: 'var(--recurring-dark)' }}>
                              <Repeat className="w-2.5 h-2.5" />
                              Recurring
                            </span>
                          )}
                          {/* Only show auto-play for video classes */}
                          {schedule.classType !== 'live' && schedule.autoPlay && (
                            <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-xs">
                              Auto
                            </span>
                          )}
                          {(studio?.settings?.bookingEnabled !== false) && (schedule.current_bookings !== undefined) && (
                            <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-xs flex items-center gap-0.5">
                              <Users className="w-2.5 h-2.5" />
                              {currentBookings}/{maxCapacity} booked
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-6 md:py-8 w-full">
                  <Video className="w-10 h-10 md:w-12 md:h-12 text-[var(--text-muted)] mx-auto mb-2" />
                  <p className="text-sm text-[var(--text-muted)] mb-3 md:mb-4">No classes scheduled</p>
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="btn btn-primary btn-sm shadow-md hover:shadow-lg transition-all duration-300"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    <span className="hidden sm:inline">Schedule Class</span>
                    <span className="sm:hidden">Add</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Schedule Modal */}
      {showAddModal && (
        <AddScheduleModal
          videos={videos?.data?.videos || videos?.data || []}
          categories={categoriesData?.data?.categories || CATEGORIES}
          selectedDate={selectedDate}
          preselectedVideoId={preselectedVideoId}
          preselectedTime={preselectedTime}
          onClose={() => {
            setShowAddModal(false);
            setPreselectedVideoId(null);
            setPreselectedTime(null);
          }}
          onSuccess={() => {
            setShowAddModal(false);
            setPreselectedVideoId(null);
            setPreselectedTime(null);
            // Don't invalidate here - the mutation already handles optimistic updates
          }}
        />
      )}

      {/* Attendees Modal */}
      {showAttendeesModal && selectedScheduleForAttendees && (
        <AttendeesModal
          schedule={selectedScheduleForAttendees}
          onClose={() => {
            setShowAttendeesModal(false);
            setSelectedScheduleForAttendees(null);
            // Schedule modal remains open in background
          }}
        />
      )}

      {/* Class Details Modal (No Booking - Business Portal) */}
      {selectedScheduleForModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => { setSelectedScheduleForModal(null); setShowCheckInQR(false); }}
        >
          <div
            className="rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl relative"
            style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => { setSelectedScheduleForModal(null); setShowCheckInQR(false); }}
              className="absolute top-4 right-4 z-10 rounded-full p-2 shadow-lg transition-all duration-200"
              style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)' }}
            >
              <X className="w-5 h-5" />
            </button>

            {/* Thumbnail banner */}
            <div className="relative h-48" style={{ background: 'linear-gradient(135deg, var(--primary-15), var(--secondary-15))' }}>
              {selectedScheduleForModal.classType === 'live' ? (
                <div className="w-full h-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, var(--primary-20), var(--primary-10))' }}>
                  <div className="text-center">
                    <Users className="w-16 h-16 mx-auto mb-2" style={{ color: 'var(--primary)' }} />
                    <span style={{ color: 'var(--primary)', fontWeight: '600' }}>LIVE CLASS</span>
                  </div>
                </div>
              ) : thumbnailsMap[selectedScheduleForModal.videoId] ? (
                <img
                  src={thumbnailsMap[selectedScheduleForModal.videoId]}
                  alt={selectedScheduleForModal.video?.title || 'Video'}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, var(--secondary-15), var(--secondary-10))' }}>
                  <Video className="w-20 h-20" style={{ color: 'var(--secondary)' }} />
                </div>
              )}

              {/* Duration Badge */}
              {(selectedScheduleForModal.classType === 'live' ? selectedScheduleForModal.liveDuration : (selectedScheduleForModal.video?.duration || selectedScheduleForModal.archivedVideoDuration)) && (
                <div className="absolute top-4 left-4 bg-black/70 backdrop-blur-sm px-3 py-2 rounded-lg flex items-center gap-2 shadow-md">
                  <Play className="w-4 h-4 text-white" />
                  <span className="font-semibold text-white text-sm">
                    {(() => {
                      const durationSeconds = selectedScheduleForModal.classType === 'live'
                        ? (selectedScheduleForModal.liveDuration || 0)
                        : (selectedScheduleForModal.video?.duration || selectedScheduleForModal.archivedVideoDuration || 0);
                      const mins = Math.floor(durationSeconds / 60);
                      const secs = durationSeconds % 60;
                      return `${mins}:${secs.toString().padStart(2, '0')}`;
                    })()}
                  </span>
                </div>
              )}

              {/* Category Badge */}
              {(selectedScheduleForModal.classType === 'live' ? selectedScheduleForModal.liveCategory : (selectedScheduleForModal.video?.category || selectedScheduleForModal.archivedVideoCategory)) && (
                <div className="absolute top-4 right-16 px-3 py-1 rounded-lg shadow-md" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                  <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {(() => {
                      const labels = {
                        yoga: 'Yoga',
                        dance: 'Dance',
                        martial_arts: 'Martial Arts',
                        fitness: 'Fitness',
                        pilates: 'Pilates',
                        meditation: 'Meditation',
                        other: 'Other'
                      };
                      const category = selectedScheduleForModal.classType === 'live'
                        ? selectedScheduleForModal.liveCategory
                        : (selectedScheduleForModal.video?.category || selectedScheduleForModal.archivedVideoCategory);
                      return labels[category] || 'Class';
                    })()}
                  </span>
                </div>
              )}

              {/* Live Class Badge */}
              {selectedScheduleForModal.classType === 'live' && (
                <div className="absolute bottom-4 left-4 px-3 py-1 rounded-lg shadow-md" style={{ backgroundColor: 'var(--primary)' }}>
                  <span className="text-sm font-semibold text-white">LIVE</span>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="p-6">
              <h2 className="text-3xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                {selectedScheduleForModal.classType === 'live'
                  ? selectedScheduleForModal.liveTitle
                  : (selectedScheduleForModal.video?.title || selectedScheduleForModal.archivedVideoTitle || 'Unknown Video')}
                {selectedScheduleForModal.videoDeleted && (
                  <span className="ml-3 text-sm font-semibold px-2 py-1 rounded" style={{ backgroundColor: 'var(--highlight-10)', color: 'var(--highlight)', border: '1px solid var(--highlight)' }}>
                    🗑️ Video Deleted
                  </span>
                )}
              </h2>

              {/* Instructor */}
              <div className="flex items-center gap-2 mb-4" style={{ color: 'var(--text-secondary)' }}>
                <span className="text-lg">
                  Instructor: {selectedScheduleForModal.classType === 'live'
                    ? selectedScheduleForModal.liveInstructorName
                    : `${selectedScheduleForModal.video?.instructor?.firstName || ''} ${selectedScheduleForModal.video?.instructor?.lastName || ''}`.trim()}
                </span>
              </div>

              {/* Recurrence Information - shown first for recurring schedules */}
              {selectedScheduleForModal.seriesId && selectedScheduleForModal.series && (
                <div className="rounded-lg p-4 mb-4" style={{ background: 'var(--recurring-10)', border: '1px solid var(--recurring-30)' }}>
                  <div className="flex items-center gap-3">
                    <Repeat className="w-6 h-6" style={{ color: 'var(--recurring)' }} />
                    <div className="flex-1">
                      <div className="text-sm font-medium mb-1" style={{ color: 'var(--recurring)' }}>Recurring Schedule</div>
                      <div className="text-base font-semibold" style={{ color: 'var(--recurring-darker)' }}>
                        {getRecurrenceDescription(selectedScheduleForModal.series.recurrencePattern, selectedScheduleForModal.series.seriesEndDate)}
                      </div>
                      {selectedScheduleForModal.series.seriesEndDate && (
                        <div className="text-xs mt-1" style={{ color: 'var(--recurring-dark)' }}>
                          Until {(() => {
                            const [year, month, day] = selectedScheduleForModal.series.seriesEndDate.split('-').map(Number);
                            return new Date(year, month - 1, day).toLocaleDateString();
                          })()}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Inline scope selector when editing */}
                  {isEditingTime && (
                    <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--recurring-30)' }}>
                      <div className="text-xs font-medium mb-2" style={{ color: 'var(--recurring)' }}>Apply changes to:</div>
                      <div className="flex flex-wrap gap-2">
                        <label
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm cursor-pointer transition-all"
                          style={{
                            background: editScope === 'this' ? 'var(--recurring)' : 'var(--bg-elevated)',
                            color: editScope === 'this' ? 'white' : 'var(--recurring-hover)',
                            border: editScope === 'this' ? 'none' : '1px solid rgba(168, 85, 247, 0.3)'
                          }}
                        >
                          <input type="radio" name="editScope" value="this" checked={editScope === 'this'} onChange={(e) => handleEditScopeChange(e.target.value)} className="sr-only" />
                          This event
                        </label>
                        <label
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm cursor-pointer transition-all"
                          style={{
                            background: editScope === 'future' ? 'var(--recurring)' : 'var(--bg-elevated)',
                            color: editScope === 'future' ? 'white' : 'var(--recurring-hover)',
                            border: editScope === 'future' ? 'none' : '1px solid rgba(168, 85, 247, 0.3)'
                          }}
                        >
                          <input type="radio" name="editScope" value="future" checked={editScope === 'future'} onChange={(e) => handleEditScopeChange(e.target.value)} className="sr-only" />
                          This & future
                        </label>
                        <label
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm cursor-pointer transition-all"
                          style={{
                            background: editScope === 'all' ? 'var(--recurring)' : 'var(--bg-elevated)',
                            color: editScope === 'all' ? 'white' : 'var(--recurring-hover)',
                            border: editScope === 'all' ? 'none' : '1px solid rgba(168, 85, 247, 0.3)'
                          }}
                        >
                          <input type="radio" name="editScope" value="all" checked={editScope === 'all'} onChange={(e) => handleEditScopeChange(e.target.value)} className="sr-only" />
                          Entire series
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Date, Time and Duration */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-[var(--highlight-5)] rounded-lg p-3">
                  <div className="flex items-center justify-between gap-2 text-[var(--text-secondary)] mb-1">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm font-medium">Date & Time</span>
                    </div>
                  </div>
                  {isEditingTime ? (
                    <div className="flex flex-col gap-2">
                      <DatePicker
                        value={editedDate}
                        onChange={setEditedDate}
                        label="Date"
                        required
                        disabled={selectedScheduleForModal?.seriesId && editScope !== 'this'}
                      />
                      {/* Show hint when date is disabled */}
                      {selectedScheduleForModal?.seriesId && editScope !== 'this' && (
                        <p className="text-xs text-[var(--text-muted)] -mt-1">
                          Date locked when editing multiple events
                        </p>
                      )}
                      <TimePicker
                        value={editedTime}
                        onChange={setEditedTime}
                        label="Time"
                        required
                      />
                    </div>
                  ) : (
                    <div>
                      <div className="text-lg font-semibold text-[var(--text-primary)]">
                        {format(parseISO(selectedScheduleForModal.specificDate), 'MMM d, yyyy')}
                      </div>
                      <div className="text-xl font-bold text-[var(--text-primary)]">
                        {formatTime12Hour(selectedScheduleForModal.startTime)}
                      </div>
                    </div>
                  )}
                </div>
                <div className="bg-[var(--highlight-5)] rounded-lg p-3">
                  <div className="flex items-center gap-2 text-[var(--text-secondary)] mb-1">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm font-medium">Duration</span>
                  </div>
                  <div className="text-xl font-bold text-[var(--text-primary)]">
                    {(() => {
                      const durationSeconds = selectedScheduleForModal.classType === 'live'
                        ? (selectedScheduleForModal.liveDuration || 0)
                        : (selectedScheduleForModal.video?.duration || selectedScheduleForModal.archivedVideoDuration || 0);
                      const minutes = Math.floor(durationSeconds / 60);
                      const seconds = durationSeconds % 60;
                      if (minutes > 0 && seconds > 0) {
                        return `${minutes}m ${seconds}s`;
                      } else if (minutes > 0) {
                        return `${minutes}m`;
                      } else if (seconds > 0) {
                        return `${seconds}s`;
                      }
                      return '0m';
                    })()}
                  </div>
                </div>
              </div>

              {/* Recurrence Options - only show for one-time schedules in edit mode */}
              {isEditingTime && !selectedScheduleForModal.seriesId && !selectedScheduleForModal.series_id && (
                <div className="mb-4 space-y-2 p-3 bg-gradient-to-br from-primary-50 to-indigo-50 rounded-xl border-2 border-[var(--border-subtle)]">
                  <label className="text-sm font-semibold text-[var(--text-primary)]">Recurrence</label>

                  {/* Recurrence Type Dropdown */}
                  <CustomDropdown
                    value={editRecurrenceType}
                    onChange={(val) => setEditRecurrenceType(val)}
                    options={[
                      { value: 'once', label: 'Does not repeat' },
                      { value: 'daily', label: 'Daily' },
                      { value: 'weekly', label: 'Weekly' },
                      { value: 'monthly', label: 'Monthly' }
                    ]}
                    placeholder="Select recurrence pattern..."
                    openUpward={true}
                  />

                  {/* Daily: Days of Week Selection */}
                  {editRecurrenceType === 'daily' && (
                    <div className="flex items-center gap-0.5">
                      <span className="text-xs font-medium mr-1" style={{ color: '#7d5e3e' }}>On:</span>
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => {
                        const isSelected = editDaysOfWeek.includes(index);
                        return (
                          <button
                            key={index}
                            type="button"
                            onClick={() => {
                              const newDays = editDaysOfWeek.includes(index)
                                ? editDaysOfWeek.filter(d => d !== index)
                                : [...editDaysOfWeek, index].sort();
                              setEditDaysOfWeek(newDays);
                            }}
                            className={`w-[48px] h-[24px] rounded-full text-[10px] font-semibold flex items-center justify-center gap-0.5 ${
                              isSelected
                                ? 'bg-primary-600 text-white'
                                : 'text-[var(--text-secondary)] border border-[var(--border-subtle)]'
                            }`}
                            style={{ background: isSelected ? undefined : 'var(--bg-elevated)' }}
                          >
                            <span>{day}</span>
                            {isSelected && <Check className="w-2.5 h-2.5" />}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Weekly: Interval */}
                  {editRecurrenceType === 'weekly' && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-medium" style={{ color: '#7d5e3e' }}>Every</span>
                      <input
                        type="number"
                        min="1"
                        max="52"
                        value={editWeeklyInterval}
                        onChange={(e) => setEditWeeklyInterval(Math.max(1, parseInt(e.target.value) || 1))}
                        style={{ width: '50px', padding: '4px', border: '1px solid #d4c3b0', borderRadius: '4px', fontSize: '12px', textAlign: 'center' }}
                      />
                      <span className="text-xs" style={{ color: '#7d5e3e' }}>week(s)</span>
                    </div>
                  )}

                  {/* Monthly: Interval + Type */}
                  {editRecurrenceType === 'monthly' && (
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium" style={{ color: '#7d5e3e' }}>Every</span>
                        <input
                          type="number"
                          min="1"
                          max="12"
                          value={editMonthlyInterval}
                          onChange={(e) => setEditMonthlyInterval(Math.max(1, parseInt(e.target.value) || 1))}
                          style={{ width: '50px', padding: '4px', border: '1px solid #d4c3b0', borderRadius: '4px', fontSize: '12px', textAlign: 'center' }}
                        />
                        <span className="text-xs" style={{ color: '#7d5e3e' }}>month(s)</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <label className="flex items-center cursor-pointer">
                          <input type="radio" name="editMonthlyType" value="date" checked={editMonthlyType === 'date'} onChange={(e) => setEditMonthlyType(e.target.value)} style={{ accentColor: '#7d5e3e' }} />
                          <span className="ml-1 text-xs" style={{ color: '#7d5e3e' }}>Same date</span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input type="radio" name="editMonthlyType" value="weekday" checked={editMonthlyType === 'weekday'} onChange={(e) => setEditMonthlyType(e.target.value)} style={{ accentColor: '#7d5e3e' }} />
                          <span className="ml-1 text-xs" style={{ color: '#7d5e3e' }}>Same day of week</span>
                        </label>
                      </div>
                    </div>
                  )}

                  {/* End Type - Only show if recurring */}
                  {editRecurrenceType !== 'once' && (
                    <div className="pt-2 space-y-1" style={{ borderTop: '1px solid #e5d4c1' }}>
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-medium" style={{ color: '#7d5e3e' }}>Ends:</span>
                        <label className="flex items-center cursor-pointer">
                          <input type="radio" name="editEndType" checked={editEndType === 'date'} onChange={() => setEditEndType('date')} style={{ accentColor: '#7d5e3e' }} />
                          <span className="ml-1 text-xs" style={{ color: '#7d5e3e' }}>On date</span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input type="radio" name="editEndType" checked={editEndType === 'never'} onChange={() => setEditEndType('never')} style={{ accentColor: '#7d5e3e' }} />
                          <span className="ml-1 text-xs" style={{ color: '#7d5e3e' }}>Never (1 year)</span>
                        </label>
                      </div>
                      {editEndType === 'date' && (
                        <DatePicker
                          value={editEndDate}
                          onChange={setEditEndDate}
                          required
                          dropdownDirection="up"
                          compact
                          minDate={editedDate}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Availability - only show when booking is enabled */}
              {(studio?.settings?.bookingEnabled !== false) && (selectedScheduleForModal.current_bookings !== undefined || selectedScheduleForModal.max_capacity !== undefined) && (
                <div className="rounded-lg p-4 mb-4" style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--border-default)' }}>
                  <div className="flex items-center gap-3">
                    <Users className="w-6 h-6" style={{ color: 'var(--primary)' }} />
                    <div>
                      <div className="text-sm font-medium" style={{ color: 'var(--primary)' }}>Class Capacity</div>
                      <div className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                        {(selectedScheduleForModal.max_capacity || 10) - (selectedScheduleForModal.current_bookings || 0)} of {selectedScheduleForModal.max_capacity || 10} spots left
                      </div>
                    </div>
                  </div>
                  {(selectedScheduleForModal.current_bookings || 0) > 0 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedScheduleForAttendees(selectedScheduleForModal);
                        setShowAttendeesModal(true);
                        // Don't close the schedule modal - keep it for back navigation
                      }}
                      className="mt-3 w-full btn btn-secondary text-sm"
                    >
                      View Attendees
                    </button>
                  )}
                </div>
              )}

              {/* Check-In QR Code for Owner - only show when booking is enabled */}
              {(studio?.settings?.bookingEnabled !== false) && selectedScheduleForModal.id && selectedScheduleForModal.specificDate && (
                <div className="rounded-lg p-4 mb-4" style={{ backgroundColor: 'var(--primary-10)', border: '1px solid var(--primary)' }}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <QrCode className="w-5 h-5" style={{ color: 'var(--primary)' }} />
                      <div>
                        <div className="text-sm font-medium" style={{ color: 'var(--primary-dark)' }}>Check-In QR Code</div>
                        <div className="text-xs" style={{ color: 'var(--primary)' }}>For customers to scan after class</div>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowCheckInQR(!showCheckInQR)}
                      className="px-3 py-1.5 text-sm font-medium rounded-lg transition-colors"
                      style={{ backgroundColor: 'var(--primary-20)', color: 'var(--primary-dark)' }}
                    >
                      {showCheckInQR ? 'Hide QR' : 'Show QR'}
                    </button>
                  </div>
                  {showCheckInQR && (
                    <div className="mt-4 flex flex-col items-center">
                      <div className="p-3 rounded-xl shadow-md" style={{ background: 'var(--bg-surface)' }}>
                        <QRCodeSVG
                          value={`https://mystudioclass.com/check-in?s=${selectedScheduleForModal.id}&d=${selectedScheduleForModal.specificDate}`}
                          size={180}
                          level="M"
                          bgColor="#ffffff"
                          fgColor="#8b5a3c"
                        />
                      </div>
                      <p className="text-xs mt-3 text-center" style={{ color: 'var(--primary)' }}>
                        Display this QR code for customers<br/>to scan after completing the class
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Badges */}
              <div className="flex items-center gap-2 flex-wrap mb-4">
                {/* Live class badge */}
                {selectedScheduleForModal.classType === 'live' && (
                  <span className="px-3 py-1.5 rounded-lg text-sm font-medium" style={{ backgroundColor: 'var(--primary-10)', color: 'var(--primary-dark)' }}>
                    In-Person Class
                  </span>
                )}
                {/* Only show auto-play for video classes */}
                {selectedScheduleForModal.classType !== 'live' && selectedScheduleForModal.autoPlay && (
                  <span className="bg-green-100 text-green-700 px-3 py-1.5 rounded-lg text-sm font-medium">
                    Auto-play Enabled
                  </span>
                )}
              </div>

              {/* Action Buttons */}
              {isEditingTime ? (
                /* Edit Mode: Save & Cancel */
                <div className="flex gap-3">
                  <button
                    onClick={handleSaveTime}
                    disabled={updateMutation.isPending || convertToRecurringMutation.isPending || (
                      editedTime === (selectedScheduleForModal.startTime?.substring(0, 5) || '') &&
                      editedDate === selectedScheduleForModal.specificDate &&
                      editRecurrenceType === 'once'
                    )}
                    className="flex-1 btn bg-green-600 hover:bg-green-700 text-white border-green-600 hover:border-green-700 py-3 text-base font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {(updateMutation.isPending || convertToRecurringMutation.isPending) ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="w-5 h-5" />
                        Save Changes
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setIsEditingTime(false);
                      setEditedTime(selectedScheduleForModal.startTime?.substring(0, 5) || '');
                      setEditedDate(selectedScheduleForModal.specificDate);
                      setEditRecurrenceType('once');
                      setEditEndDate('');
                    }}
                    disabled={updateMutation.isPending || convertToRecurringMutation.isPending}
                    className="flex-1 btn bg-[var(--highlight-10)] hover:bg-[var(--highlight-15)] text-[var(--text-secondary)] border-[var(--border-subtle)] hover:border-[var(--border-muted)] py-3 text-base font-semibold flex items-center justify-center gap-2"
                  >
                    <X className="w-5 h-5" />
                    Cancel
                  </button>
                </div>
              ) : (
                /* View Mode: Edit & Delete */
                <div className="flex gap-3">
                  <button
                    onClick={() => setIsEditingTime(true)}
                    className="flex-1 btn bg-blue-600 hover:bg-blue-700 text-white border-blue-600 hover:border-blue-700 py-3 text-base font-semibold flex items-center justify-center gap-2"
                  >
                    <Edit className="w-5 h-5" />
                    Edit Schedule
                  </button>
                  <button
                    onClick={() => {
                      setScheduleToDelete(selectedScheduleForModal);
                      setShowDeleteConfirm(true);
                    }}
                    disabled={deleteMutation.isPending}
                    className="flex-1 btn bg-red-600 hover:bg-red-700 text-white border-red-600 hover:border-red-700 py-3 text-base font-semibold flex items-center justify-center gap-2"
                  >
                    {deleteMutation.isPending ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-5 h-5" />
                        Delete
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && scheduleToDelete && (
        <DeleteConfirmationModal
          schedule={scheduleToDelete}
          onConfirm={confirmDelete}
          onCancel={() => {
            setShowDeleteConfirm(false);
            setScheduleToDelete(null);
          }}
          isDeleting={deleteMutation.isPending || deleteSeriesFromInstanceMutation.isPending || deleteSeriesMutation.isPending}
          deleteScope={deleteScope}
          setDeleteScope={setDeleteScope}
        />
      )}

      {/* Edit Confirmation Modal */}
      {showEditConfirm && selectedScheduleForModal && (
        <EditConfirmationModal
          schedule={selectedScheduleForModal}
          newTime={editedTime}
          newDate={editedDate}
          pendingEdit={pendingEdit}
          onConfirm={confirmEdit}
          onCancel={() => {
            setShowEditConfirm(false);
            setPendingEdit(null);
            setEditScope('this'); // Reset to default
          }}
          isSaving={updateMutation.isPending || updateSeriesFromInstanceMutation.isPending || updateSeriesMutation.isPending}
          editScope={editScope}
        />
      )}
    </div>
  );
}

// Delete Confirmation Modal Component with Attendee List
function DeleteConfirmationModal({ schedule, onConfirm, onCancel, isDeleting, deleteScope, setDeleteScope }) {
  const { data: bookingsData, isLoading } = useQuery({
    queryKey: ['bookings', schedule.id],
    queryFn: () => bookingsAPI.getClassBookings(schedule.id),
    enabled: !!schedule.id,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  });

  const attendees = bookingsData?.data?.filter(b => b.status === 'confirmed') || [];
  const isRecurring = schedule.seriesId;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-2 md:p-4">
      <div className="rounded-2xl max-w-2xl w-full p-4 md:p-6 shadow-xl max-h-[95vh] md:max-h-[90vh] overflow-y-auto" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}>
        <h2 className="text-xl md:text-2xl font-bold text-red-600 mb-3 md:mb-4">Confirm Delete</h2>
        <p className="text-sm md:text-base text-[var(--text-secondary)] mb-2">Are you sure you want to delete:</p>

        <div className="p-3 md:p-4 rounded-lg mb-3 md:mb-4 space-y-1 md:space-y-2" style={{ background: 'var(--bg-elevated)' }}>
          <p className="font-semibold text-base md:text-lg text-[var(--text-primary)]">{schedule.video?.title || schedule.archivedVideoTitle || 'Untitled Video'}</p>
          <p className="text-[var(--text-secondary)]">
            <strong>Date:</strong> {schedule.specificDate ? format(parseISO(schedule.specificDate), 'MMMM d, yyyy') : 'Not specified'}
          </p>
          <p className="text-[var(--text-secondary)]">
            <strong>Time:</strong> {schedule.startTime ? formatTime12Hour(schedule.startTime) : 'Not specified'}
          </p>
          <p className="text-[var(--text-secondary)]">
            <strong>Category:</strong> {schedule.video?.category || 'Not specified'}
          </p>
        </div>

        {isRecurring && (
          <div className="mb-4">
            <p className="text-sm font-semibold text-[var(--text-secondary)] mb-3">This is a recurring schedule. What would you like to delete?</p>
            <div className="space-y-2">
              <label className="flex items-start p-3 border-2 rounded-lg cursor-pointer transition-colors"
                style={{ borderColor: deleteScope === 'this' ? 'var(--recurring-hover)' : 'var(--border-subtle)', background: 'var(--bg-elevated)' }}>
                <input
                  type="radio"
                  name="deleteScope"
                  value="this"
                  checked={deleteScope === 'this'}
                  onChange={(e) => setDeleteScope(e.target.value)}
                  className="mt-1 mr-3"
                  style={{ accentColor: 'var(--recurring-hover)' }}
                />
                <div>
                  <p className="font-medium text-[var(--text-primary)]">This event only</p>
                  <p className="text-sm text-[var(--text-secondary)]">Only delete this specific instance</p>
                </div>
              </label>

              <label className="flex items-start p-3 border-2 rounded-lg cursor-pointer transition-colors"
                style={{ borderColor: deleteScope === 'future' ? 'var(--recurring-hover)' : 'var(--border-subtle)', background: 'var(--bg-elevated)' }}>
                <input
                  type="radio"
                  name="deleteScope"
                  value="future"
                  checked={deleteScope === 'future'}
                  onChange={(e) => setDeleteScope(e.target.value)}
                  className="mt-1 mr-3"
                  style={{ accentColor: 'var(--recurring-hover)' }}
                />
                <div>
                  <p className="font-medium text-[var(--text-primary)]">This and following events</p>
                  <p className="text-sm text-[var(--text-secondary)]">Delete this event and all future events in the series</p>
                </div>
              </label>

              <label className="flex items-start p-3 border-2 rounded-lg cursor-pointer transition-colors"
                style={{ borderColor: deleteScope === 'all' ? 'var(--recurring-hover)' : 'var(--border-subtle)', background: 'var(--bg-elevated)' }}>
                <input
                  type="radio"
                  name="deleteScope"
                  value="all"
                  checked={deleteScope === 'all'}
                  onChange={(e) => setDeleteScope(e.target.value)}
                  className="mt-1 mr-3"
                  style={{ accentColor: 'var(--recurring-hover)' }}
                />
                <div>
                  <p className="font-medium text-[var(--text-primary)]">All events in the series</p>
                  <p className="text-sm text-[var(--text-secondary)]">Delete all past and future events in the series</p>
                </div>
              </label>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600 border-t-transparent mx-auto"></div>
            <p className="text-sm text-[var(--text-muted)] mt-2">Loading attendees...</p>
          </div>
        ) : attendees.length > 0 ? (
          <>
            <div className="p-4 mb-4" style={{ backgroundColor: '#fef2f2', borderLeft: '4px solid #ef4444' }}>
              <p className="font-bold mb-2" style={{ color: '#991b1b' }}>
                🚨 {attendees.length} booking{attendees.length !== 1 ? 's' : ''} will be CANCELLED
              </p>
              <p className="text-sm mb-2" style={{ color: '#991b1b' }}>
                Deleting this class will automatically cancel all bookings.
              </p>
              <p className="text-sm font-bold mb-3" style={{ color: '#991b1b', backgroundColor: '#fee2e2', padding: '8px', borderRadius: '6px' }}>
                ⚠️ Please manually notify all customers immediately:
              </p>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {attendees.map((booking) => (
                  <div key={booking.id} className="p-3 rounded" style={{ background: 'var(--bg-elevated)', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                    <p className="font-semibold text-[var(--text-primary)]">{booking.student?.firstName} {booking.student?.lastName}</p>
                    {booking.student?.email && (
                      <p className="text-sm text-[var(--text-secondary)]">📧 {booking.student.email}</p>
                    )}
                    {booking.student?.phone && (
                      <p className="text-sm text-[var(--text-secondary)]">📱 {booking.student.phone}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : null}

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 bg-[var(--highlight-10)] text-[var(--text-secondary)] rounded-lg hover:bg-[var(--highlight-15)] transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50"
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Edit Confirmation Modal Component
function EditConfirmationModal({ schedule, newTime, newDate, pendingEdit, onConfirm, onCancel, isSaving, editScope }) {
  const { data: bookingsData, isLoading } = useQuery({
    queryKey: ['bookings', schedule.id],
    queryFn: () => bookingsAPI.getClassBookings(schedule.id),
    enabled: !!schedule.id,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  });

  const attendees = bookingsData?.data?.filter(b => b.status === 'confirmed') || [];
  // Filter to only non-checked-in bookings (these will be cancelled)
  const bookingsToCancel = attendees.filter(b => !b.checkedInAt);
  const isRecurring = schedule.seriesId;
  const isConvertingToRecurring = pendingEdit?.isConvertingToRecurring;

  // Check if date or time is actually changing
  const isDateChanging = newDate && newDate !== schedule.specificDate;
  const isTimeChanging = newTime && newTime !== schedule.startTime;
  const isDateTimeChanging = isDateChanging || isTimeChanging;

  // Helper to format recurrence type for display
  const formatRecurrenceType = (type, daysOfWeek, weeklyInterval, monthlyInterval, monthlyType) => {
    if (type === 'once') return 'One-time (no repeat)';
    if (type === 'daily') {
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const selectedDays = (daysOfWeek || []).map(d => dayNames[d]).join(', ');
      return `Weekly on ${selectedDays || 'selected days'}`;
    }
    if (type === 'weekly') {
      const interval = weeklyInterval || 1;
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const selectedDays = (daysOfWeek || []).map(d => dayNames[d]).join(', ');
      return interval === 1 ? `Every week on ${selectedDays}` : `Every ${interval} weeks on ${selectedDays}`;
    }
    if (type === 'monthly') {
      const interval = monthlyInterval || 1;
      const typeLabel = monthlyType === 'dayOfMonth' ? 'same date' : 'same week/day';
      return interval === 1 ? `Monthly (${typeLabel})` : `Every ${interval} months (${typeLabel})`;
    }
    return type;
  };

  // Format end date info
  const formatEndInfo = () => {
    if (!pendingEdit) return null;
    if (pendingEdit.endType === 'never') return 'Repeats indefinitely';
    if (pendingEdit.endType === 'date' && pendingEdit.endDate) {
      return `Until ${format(parseISO(pendingEdit.endDate), 'MMM d, yyyy')}`;
    }
    return null;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-2 md:p-4">
      <div className="rounded-2xl max-w-2xl w-full p-4 md:p-6 shadow-xl max-h-[95vh] md:max-h-[90vh] overflow-y-auto" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}>
        <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4" style={{ color: 'var(--primary)' }}>Confirm Schedule Change</h2>
        <p className="text-sm md:text-base text-[var(--text-secondary)] mb-2">You are about to change the schedule for:</p>

        <div className="p-3 md:p-4 rounded-lg mb-3 md:mb-4 space-y-2" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
          <p className="font-semibold text-base md:text-lg" style={{ color: 'var(--text-primary)' }}>
            {schedule.classType === 'live' ? (schedule.liveTitle || 'Live Class') : (schedule.video?.title || schedule.archivedVideoTitle || 'Untitled Video')}
            {schedule.classType === 'live' && <span className="ml-2 text-sm px-2 py-0.5 rounded" style={{ background: 'var(--primary-10)', color: 'var(--primary-dark)' }}>LIVE</span>}
          </p>
          <div className="grid grid-cols-2 gap-2 md:gap-4 mt-2">
            <div>
              <p className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Current:</p>
              <p style={{ color: 'var(--text-primary)' }}>
                <strong>Date:</strong> {schedule.specificDate ? format(parseISO(schedule.specificDate), 'MMM d, yyyy') : 'Not specified'}
              </p>
              <p style={{ color: 'var(--text-primary)' }}>
                <strong>Time:</strong> {schedule.startTime ? formatTime12Hour(schedule.startTime) : 'Not specified'}
              </p>
              <p style={{ color: 'var(--text-primary)' }}>
                <strong>Repeat:</strong> {isRecurring ? 'Recurring' : 'One-time'}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium" style={{ color: 'var(--primary)' }}>New:</p>
              <p style={{ color: 'var(--primary)' }}>
                <strong>Date:</strong> {newDate ? format(parseISO(newDate), 'MMM d, yyyy') : 'Not specified'}
              </p>
              <p style={{ color: 'var(--primary)' }}>
                <strong>Time:</strong> {newTime ? formatTime12Hour(newTime) : 'Not specified'}
              </p>
              {pendingEdit && (
                <p style={{ color: 'var(--primary)' }}>
                  <strong>Repeat:</strong> {formatRecurrenceType(
                    pendingEdit.recurrenceType,
                    pendingEdit.daysOfWeek,
                    pendingEdit.weeklyInterval,
                    pendingEdit.monthlyInterval,
                    pendingEdit.monthlyType
                  )}
                </p>
              )}
            </div>
          </div>
          <p style={{ color: 'var(--text-primary)' }} className="mt-2">
            <strong>Category:</strong> {schedule.classType === 'live' ? (schedule.liveCategory || 'Not specified') : (schedule.video?.category || 'Not specified')}
          </p>
        </div>

        {/* Converting to recurring - highlight this change */}
        {isConvertingToRecurring && (
          <div className="rounded-lg p-3 mb-4" style={{ backgroundColor: 'var(--recurring-10)', border: '1px solid var(--recurring-30)' }}>
            <div className="flex items-center gap-2">
              <Repeat className="w-5 h-5" style={{ color: 'var(--recurring)' }} />
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--recurring-darker)' }}>
                  Converting to recurring schedule
                </p>
                <p className="text-xs mt-1" style={{ color: 'var(--recurring-dark)' }}>
                  {formatRecurrenceType(
                    pendingEdit.recurrenceType,
                    pendingEdit.daysOfWeek,
                    pendingEdit.weeklyInterval,
                    pendingEdit.monthlyInterval,
                    pendingEdit.monthlyType
                  )}
                  {formatEndInfo() && ` • ${formatEndInfo()}`}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Recurring schedule scope summary (scope was already selected inline) */}
        {isRecurring && !isConvertingToRecurring && (
          <div className="rounded-lg p-3 mb-4" style={{ backgroundColor: 'var(--recurring-10)', border: '1px solid var(--recurring-30)' }}>
            <div className="flex items-center gap-2">
              <Repeat className="w-5 h-5" style={{ color: 'var(--recurring)' }} />
              <p className="text-sm" style={{ color: 'var(--recurring-darker)' }}>
                <span className="font-medium">Applying to: </span>
                {editScope === 'this' && 'This event only'}
                {editScope === 'future' && 'This and all future events'}
                {editScope === 'all' && 'Entire series'}
              </p>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-2 mx-auto" style={{ borderColor: 'var(--primary)', borderTopColor: 'transparent' }}></div>
            <p className="text-sm text-[var(--text-muted)] mt-2">Loading attendees...</p>
          </div>
        ) : isDateTimeChanging && bookingsToCancel.length > 0 ? (
          <>
            <div className="p-4 mb-4" style={{ backgroundColor: '#fef2f2', borderLeft: '4px solid #ef4444' }}>
              <p className="font-bold mb-2" style={{ color: '#991b1b' }}>
                🚨 {bookingsToCancel.length} booking{bookingsToCancel.length !== 1 ? 's' : ''} will be CANCELLED
              </p>
              <p className="text-sm mb-2" style={{ color: '#991b1b' }}>
                Changing the date or time will automatically cancel these bookings.
              </p>
              <p className="text-sm font-bold mb-3" style={{ color: '#991b1b', backgroundColor: '#fee2e2', padding: '8px', borderRadius: '6px' }}>
                ⚠️ Please manually notify all customers immediately:
              </p>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {bookingsToCancel.map((booking) => (
                  <div key={booking.id} className="p-3 rounded" style={{ background: 'var(--bg-elevated)', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                    <p className="font-semibold text-[var(--text-primary)]">{booking.student?.firstName} {booking.student?.lastName}</p>
                    {booking.student?.email && (
                      <p className="text-sm text-[var(--text-secondary)]">📧 {booking.student.email}</p>
                    )}
                    {booking.student?.phone && (
                      <p className="text-sm text-[var(--text-secondary)]">📱 {booking.student.phone}</p>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs mt-3" style={{ color: '#7f1d1d' }}>
                Customers must re-book if they want to attend the rescheduled class.
              </p>
            </div>
          </>
        ) : attendees.length > 0 && !isDateTimeChanging ? (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
            <p className="text-yellow-800 text-sm">
              <span className="font-semibold">ℹ️ {attendees.length} student{attendees.length !== 1 ? 's' : ''} enrolled</span> - Bookings will remain intact since date/time is not changing.
            </p>
          </div>
        ) : null}

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 bg-[var(--highlight-10)] text-[var(--text-secondary)] rounded-lg hover:bg-[var(--highlight-15)] transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isSaving}
            className="flex-1 px-4 py-2 text-white rounded-lg transition-colors font-medium disabled:opacity-50"
            style={{ backgroundColor: 'var(--primary)' }}
            onMouseEnter={(e) => !isSaving && (e.target.style.backgroundColor = 'var(--primary-dark)')}
            onMouseLeave={(e) => !isSaving && (e.target.style.backgroundColor = 'var(--primary)')}
          >
            {isSaving ? 'Saving...' : 'Confirm Change'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Attendees Modal Component
function AttendeesModal({ schedule, onClose }) {
  const { data: bookingsData, isLoading } = useQuery({
    queryKey: ['class-bookings', schedule.id],
    queryFn: () => bookingsAPI.getClassBookings(schedule.id),
    enabled: !!schedule.id,
    refetchOnWindowFocus: true,
    refetchInterval: false,
  });

  const bookings = bookingsData?.data || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-2 md:p-4">
      <div className="rounded-2xl max-w-md w-full p-4 md:p-6 max-h-[95vh] md:max-h-[90vh] overflow-y-auto shadow-xl" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}>
        <div className="flex items-center justify-between mb-3 md:mb-4">
          <h2 className="text-xl md:text-2xl font-bold text-[var(--text-primary)]">Class Attendees</h2>
          <button
            onClick={onClose}
            className="text-[var(--text-secondary)] hover:text-[var(--text-secondary)] p-1 hover:bg-[var(--highlight-5)] rounded-lg transition-all duration-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-4 pb-3 border-b border-[var(--border-subtle)]">
          <h3 className="font-semibold text-[var(--text-primary)]">{schedule.classType === 'live' ? schedule.liveTitle : (schedule.video?.title || schedule.archivedVideoTitle || 'Unknown Class')}</h3>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            {formatTime12Hour(schedule.startTime)}{(studio?.settings?.bookingEnabled !== false) && ` • ${schedule.current_bookings || 0} / ${schedule.max_capacity || 10} spots`}
          </p>
        </div>

        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-[var(--text-secondary)] mt-2">Loading attendees...</p>
          </div>
        ) : bookings.length > 0 ? (
          <div className="space-y-3">
            {bookings.map((booking) => (
              <div
                key={booking.id}
                className="p-3 bg-[var(--highlight-5)] rounded-lg border border-[var(--border-subtle)] hover:bg-[var(--highlight-10)] hover:shadow-md transition-all duration-300"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[var(--highlight-10)] rounded-full flex items-center justify-center text-[var(--text-primary)] font-semibold flex-shrink-0 border border-[var(--border-subtle)]">
                    {booking.student?.firstName?.[0]?.toUpperCase() || '?'}
                    {booking.student?.lastName?.[0]?.toUpperCase() || ''}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[var(--text-primary)] truncate">
                      {booking.student?.firstName} {booking.student?.lastName}
                    </p>
                    {booking.student?.email && (
                      <p className="text-xs text-[var(--text-secondary)] truncate">{booking.student.email}</p>
                    )}
                    {booking.student?.phone && (
                      <p className="text-xs text-[var(--text-muted)] truncate">{booking.student.phone}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Users className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
            <p className="text-[var(--text-secondary)]">No attendees yet</p>
            <p className="text-sm text-[var(--text-muted)] mt-1">Bookings will appear here once customers sign up</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Add Schedule Modal Component
function AddScheduleModal({ videos, categories, selectedDate, preselectedVideoId, preselectedTime, onClose, onSuccess }) {
  const queryClient = useQueryClient();
  const { studio, user } = useAuth();
  const studioId = studio?.id || user?.studioId;
  const [selectedCategory, setSelectedCategory] = useState('');
  const [classType, setClassType] = useState('video'); // 'video' or 'live'
  const [formData, setFormData] = useState({
    videoId: preselectedVideoId || '',
    specificDate: format(selectedDate, 'yyyy-MM-dd'),
    startTime: preselectedTime || '09:00',
    autoPlay: true,
    // Recurring schedule fields
    recurrenceType: 'once',
    daysOfWeek: [],
    weeklyInterval: 1,
    monthlyInterval: 1,
    monthlyType: 'date', // 'date' or 'weekday'
    yearlyInterval: 1,
    endType: 'date', // 'date' (default - requires end date) or 'never' (repeat indefinitely)
    endDate: null, // User must select an end date (unless they check "Repeat Indefinitely")
    // Live class fields
    liveTitle: '',
    liveCategory: '',
    liveInstructorName: '',
    liveDuration: 60, // minutes
    liveDescription: ''
  });

  // Fetch existing schedules to check for conflicts
  const { data: existingSchedules } = useQuery({
    queryKey: ['schedules', studioId],
    queryFn: () => schedulesAPI.getSchedules(studioId),
    enabled: !!studioId
  });

  // Set preselected video when component mounts or preselectedVideoId changes
  useEffect(() => {
    if (preselectedVideoId && videos && videos.length > 0) {
      const video = videos.find(v => v.id === preselectedVideoId);
      if (video) {
        setFormData(prev => ({ ...prev, videoId: preselectedVideoId }));
        setSelectedCategory(video.category || '');
      }
    }
  }, [preselectedVideoId, videos]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Filter videos by selected category
  const filteredVideos = useMemo(() => {
    if (!selectedCategory) return [];
    if (!videos || !Array.isArray(videos) || videos.length === 0) {
      return [];
    }

    return videos.filter(video => {
      // Handle both camelCase and snake_case if needed
      const videoCategory = video.category || video.Category;
      return videoCategory === selectedCategory;
    });
  }, [videos, selectedCategory]);

  // Reset video selection when category changes
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setFormData(prev => ({ ...prev, videoId: '' }));
  };

  // Check for scheduling conflicts
  const checkForConflicts = () => {
    const startTime = formData.startTime;
    const specificDate = formData.specificDate;

    if (!startTime || !specificDate) {
      return null;
    }

    // Determine duration based on class type
    let newClassDuration = 0; // Duration in seconds

    if (classType === 'live') {
      // Live class - liveDuration is in minutes, convert to seconds
      newClassDuration = (formData.liveDuration || 60) * 60;
    } else {
      // Video class - need videoId and video duration
      if (!formData.videoId) return null;
      const selectedVideo = videos?.find(v => v.id === formData.videoId);
      if (!selectedVideo) return null;
      newClassDuration = selectedVideo.duration || 0;
    }

    // Convert new schedule start time to minutes since midnight
    const [newHours, newMinutes] = startTime.split(':').map(Number);
    const newStartMinutes = newHours * 60 + newMinutes;
    const newEndMinutes = newStartMinutes + Math.ceil(newClassDuration / 60);

    // Calculate dates for checking
    const currentDate = parseISO(specificDate);
    const currentDateStr = format(currentDate, 'yyyy-MM-dd');

    // Calculate previous day (to check for classes extending past midnight into current day)
    const previousDay = new Date(currentDate);
    previousDay.setDate(previousDay.getDate() - 1);
    const previousDayStr = format(previousDay, 'yyyy-MM-dd');

    // Calculate next day (to check if new schedule extends into next day)
    const nextDay = new Date(currentDate);
    nextDay.setDate(nextDay.getDate() + 1);
    const nextDayStr = format(nextDay, 'yyyy-MM-dd');

    // Get schedules for current date, previous date, and next date from existingSchedules
    const schedulesToCheck = existingSchedules?.data?.schedules?.filter(schedule => {
      if (!schedule.specificDate) return false;
      const scheduleDate = format(parseISO(schedule.specificDate), 'yyyy-MM-dd');
      return scheduleDate === currentDateStr || scheduleDate === previousDayStr || scheduleDate === nextDayStr;
    }) || [];

    // Check each existing schedule for overlap
    for (const schedule of schedulesToCheck) {
      // Handle both video and live classes for existing schedule duration
      const isExistingLiveClass = schedule.classType === 'live';
      const existingDuration = isExistingLiveClass
        ? (schedule.liveDuration || 3600) // Live class default 1 hour
        : (schedule.video?.duration || 0);

      const [existingHours, existingMinutes] = (schedule.startTime || '00:00:00').split(':').map(Number);
      const existingStartMinutes = existingHours * 60 + existingMinutes;
      const existingEndMinutes = existingStartMinutes + Math.ceil(existingDuration / 60);

      const scheduleDate = format(parseISO(schedule.specificDate), 'yyyy-MM-dd');

      // Get title for conflict message (handle both video and live classes)
      const conflictTitle = isExistingLiveClass
        ? (schedule.liveTitle || 'Live class')
        : (schedule.video?.title || schedule.archivedVideoTitle || 'Another class');

      if (scheduleDate === previousDayStr) {
        // Schedule from previous day - only conflicts if it extends past midnight
        if (existingEndMinutes > 1440) {
          const extendIntoCurrentDay = existingEndMinutes - 1440;
          // Check if new schedule starts before previous-day schedule ends
          if (newStartMinutes < extendIntoCurrentDay) {
            return {
              conflictingSchedule: schedule,
              conflictTitle,
              existingStart: `${String(existingHours).padStart(2, '0')}:${String(existingMinutes).padStart(2, '0')} (previous day)`,
              existingEnd: `${String(Math.floor(extendIntoCurrentDay / 60)).padStart(2, '0')}:${String(extendIntoCurrentDay % 60).padStart(2, '0')} (current day)`
            };
          }
        }
      } else if (scheduleDate === nextDayStr) {
        // Schedule on next day - only conflicts if new schedule extends past midnight
        if (newEndMinutes > 1440) {
          const newExtendIntoNextDay = newEndMinutes - 1440;
          // Check if existing schedule starts before new schedule ends in next day
          if (existingStartMinutes < newExtendIntoNextDay) {
            return {
              conflictingSchedule: schedule,
              conflictTitle,
              existingStart: `${String(existingHours).padStart(2, '0')}:${String(existingMinutes).padStart(2, '0')} (next day)`,
              existingEnd: `${String(Math.floor(existingEndMinutes / 60)).padStart(2, '0')}:${String(existingEndMinutes % 60).padStart(2, '0')}`
            };
          }
        }
      } else {
        // Schedule on current day - normal overlap check
        if (newStartMinutes < existingEndMinutes && newEndMinutes > existingStartMinutes) {
          return {
            conflictingSchedule: schedule,
            conflictTitle,
            existingStart: `${String(existingHours).padStart(2, '0')}:${String(existingMinutes).padStart(2, '0')}`,
            existingEnd: `${String(Math.floor(existingEndMinutes / 60)).padStart(2, '0')}:${String(existingEndMinutes % 60).padStart(2, '0')}`
          };
        }
      }
    }

    return null; // No conflicts found
  };

  const createMutation = useMutation({
    mutationFn: async (data) => {
      try {
        // Use recurring series API if recurrence type is not "once"
        const response = data.recurrenceType !== 'once'
          ? await schedulesAPI.createRecurringSeries(data)
          : await schedulesAPI.createSchedule(data);
        return response;
      } catch (error) {
        console.error('Failed to create schedule:', error);
        // Check if it's actually a success but wrapped in error
        if (error.response?.data?.success === true) {
          return error.response.data;
        }
        throw error;
      }
    },
    onMutate: async (newSchedule) => {
      // Cancel outgoing refetches for all schedule queries
      await queryClient.cancelQueries({ queryKey: ['schedules'] });

      // Snapshot all schedule queries to rollback if needed
      const previousQueries = new Map();

      // Get all existing schedule queries
      queryClient.getQueriesData({ queryKey: ['schedules'] }).forEach(([key, data]) => {
        previousQueries.set(JSON.stringify(key), data);
      });

      // Create optimistic schedule
      const tempId = 'temp-' + Date.now();
      const selectedVideo = videos?.find(v => v.id === newSchedule.videoId);
      const isLiveClass = newSchedule.classType === 'live';

      const optimisticSchedule = {
        id: tempId,
        videoId: newSchedule.videoId,
        specificDate: newSchedule.specificDate,
        startTime: newSchedule.startTime,
        dayOfWeek: newSchedule.dayOfWeek,
        isActive: true,
        maxCapacity: studio?.settings?.defaultMaxCapacity || 15,
        max_capacity: studio?.settings?.defaultMaxCapacity || 15,
        current_bookings: 0, // New schedule has no bookings yet
        // Live class properties
        classType: newSchedule.classType || 'video',
        liveTitle: newSchedule.liveTitle,
        liveInstructorName: newSchedule.liveInstructorName,
        liveCategory: newSchedule.liveCategory,
        liveDuration: newSchedule.liveDuration,
        liveDescription: newSchedule.liveDescription,
        // Video properties (only for video classes)
        video: !isLiveClass && selectedVideo ? {
          id: selectedVideo.id,
          title: selectedVideo.title,
          duration: selectedVideo.duration,
          thumbnailUrl: selectedVideo.thumbnailUrl,
          instructor: selectedVideo.instructor
        } : null,
        _optimistic: true
      };

      // Optimistically update ALL schedule queries that contain this studio's data
      queryClient.getQueriesData({ queryKey: ['schedules'] }).forEach(([key, data]) => {
        // Only update queries that have schedule data
        if (data?.data?.schedules && Array.isArray(data.data.schedules)) {
          queryClient.setQueryData(key, {
            ...data,
            data: {
              ...data.data,
              schedules: [...data.data.schedules, optimisticSchedule]
            }
          });
        }
      });

      return { previousQueries };
    },
    onSuccess: (response) => {
      // Check if response indicates success
      if (response?.success === true || response?.data?.schedule) {
        const realSchedule = response.data?.schedule;

        if (realSchedule) {
          // Replace optimistic schedule with real one in all queries
          queryClient.setQueriesData({ queryKey: ['schedules'] }, (oldData) => {
            if (!oldData?.data?.schedules) return oldData;

            return {
              ...oldData,
              data: {
                ...oldData.data,
                schedules: oldData.data.schedules.map(s => {
                  // Replace the optimistic schedule with the real one
                  if (s._optimistic || String(s.id).startsWith('temp-')) {
                    return realSchedule;
                  }
                  return s;
                })
              }
            };
          });
        }

        toast.success('Video scheduled successfully!');

        // Refetch countdown
        queryClient.refetchQueries({
          queryKey: ['upcoming-schedules-countdown'],
          type: 'active'
        });
      } else {
        console.warn('SchedulePage - Unexpected response format:', response);
        toast.error('Schedule may have been created, but received unexpected response');

        // Fallback: refetch if we don't have the schedule in response
        queryClient.refetchQueries({
          queryKey: ['schedules'],
          type: 'active'
        });
      }
    },
    onError: (error, newSchedule, context) => {
      // Rollback optimistic updates for all affected queries
      if (context?.previousQueries) {
        context.previousQueries.forEach((data, keyString) => {
          const key = JSON.parse(keyString);
          queryClient.setQueryData(key, data);
        });
      }

      console.error('Failed to create schedule:', error);
      // Check if the schedule was actually created despite the error
      if (error.response?.data?.success === true || error.response?.data?.data?.schedule) {
        queryClient.refetchQueries({
          queryKey: ['schedules'],
          type: 'active'
        });
        queryClient.refetchQueries({
          queryKey: ['upcoming-schedules-countdown'],
          type: 'active'
        });
        toast.success('Video scheduled successfully!');
        onSuccess();
      } else {
        toast.error(error.response?.data?.message || error.message || 'Failed to schedule video');
      }
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validation based on class type
    if (classType === 'video') {
      if (!selectedCategory) {
        toast.error('Please select a category');
        return;
      }
      if (!formData.videoId) {
        toast.error('Please select a video');
        return;
      }
    } else {
      // Live class validation
      if (!formData.liveTitle) {
        toast.error('Please enter a class title');
        return;
      }
      if (!formData.liveCategory) {
        toast.error('Please select a category');
        return;
      }
      if (!formData.liveInstructorName) {
        toast.error('Please enter an instructor name');
        return;
      }
      if (!formData.liveDuration || formData.liveDuration <= 0) {
        toast.error('Please enter a valid duration');
        return;
      }
    }

    // Check for scheduling conflicts
    const conflict = checkForConflicts();
    if (conflict) {
      // Use pre-computed conflictTitle from checkForConflicts, fallback for backwards compatibility
      const title = conflict.conflictTitle || conflict.conflictingSchedule?.video?.title || conflict.conflictingSchedule?.liveTitle || 'Another class';
      toast.error(
        `Scheduling conflict! ${title} is scheduled from ${conflict.existingStart} to ${conflict.existingEnd}. Please choose a different time.`,
        { duration: 5000 }
      );
      return;
    }

    // Calculate dayOfWeek from specificDate
    let dayOfWeek = null;
    if (formData.specificDate) {
      const scheduleDate = parseISO(formData.specificDate);
      dayOfWeek = scheduleDate.getDay();
    }

    // Build submit data based on recurrence type
    if (formData.recurrenceType === 'once') {
      // Regular single schedule
      const submitData = {
        classType,
        startTime: formData.startTime,
        autoPlay: formData.autoPlay,
        specificDate: formData.specificDate,
        dayOfWeek: dayOfWeek,
        recurrenceType: 'once'
      };

      // Add type-specific fields
      if (classType === 'video') {
        submitData.videoId = formData.videoId;
      } else {
        submitData.liveTitle = formData.liveTitle;
        submitData.liveCategory = formData.liveCategory;
        submitData.liveInstructorName = formData.liveInstructorName;
        submitData.liveDuration = formData.liveDuration * 60; // Convert minutes to seconds
        submitData.liveDescription = formData.liveDescription;
      }

      createMutation.mutate(submitData);
      // Close modal immediately - optimistic update already shows schedule
      onClose();
    } else {
      // Recurring schedule series
      // Validate days of week for daily only (weekly uses start date's day automatically)
      if (formData.recurrenceType === 'daily' && formData.daysOfWeek.length === 0) {
        toast.error('Please select at least one day of the week');
        return;
      }

      // Validate end date is selected (unless repeating indefinitely)
      if (formData.endType === 'date' && !formData.endDate) {
        toast.error('Please select an end date or check "Repeat Indefinitely"');
        return;
      }

      // Build recurrence pattern based on type
      let recurrencePattern = {};
      const scheduleDate = parseISO(formData.specificDate);

      if (formData.recurrenceType === 'daily') {
        // Daily with selected days - use weekly pattern with interval 1
        recurrencePattern = {
          type: 'weekly',
          daysOfWeek: formData.daysOfWeek,
          interval: 1
        };
      } else if (formData.recurrenceType === 'weekly') {
        // Weekly repeats on the same day of week as the start date
        recurrencePattern = {
          type: 'weekly',
          daysOfWeek: [scheduleDate.getDay()],
          interval: formData.weeklyInterval
        };
      } else if (formData.recurrenceType === 'monthly') {
        if (formData.monthlyType === 'date') {
          recurrencePattern = {
            type: 'monthly',
            monthlyType: 'date',
            dayOfMonth: scheduleDate.getDate(),
            interval: formData.monthlyInterval
          };
        } else {
          const weekOfMonth = Math.ceil(scheduleDate.getDate() / 7);
          recurrencePattern = {
            type: 'monthly',
            monthlyType: 'weekday',
            weekdayPosition: weekOfMonth,
            weekday: scheduleDate.getDay(),
            interval: formData.monthlyInterval
          };
        }
      } else if (formData.recurrenceType === 'yearly') {
        recurrencePattern = {
          type: 'yearly',
          dayOfMonth: scheduleDate.getDate(),
          month: scheduleDate.getMonth(), // 0-indexed (0 = January)
          interval: formData.yearlyInterval
        };
      }

      const submitData = {
        classType,
        startTime: formData.startTime,
        autoPlay: formData.autoPlay,
        seriesStartDate: formData.specificDate,
        seriesEndDate: formData.endType === 'date' ? formData.endDate : null,
        recurrencePattern: recurrencePattern
      };

      // Add type-specific fields
      if (classType === 'video') {
        submitData.videoId = formData.videoId;
      } else {
        submitData.liveTitle = formData.liveTitle;
        submitData.liveCategory = formData.liveCategory;
        submitData.liveInstructorName = formData.liveInstructorName;
        submitData.liveDuration = formData.liveDuration * 60; // Convert minutes to seconds
        submitData.liveDescription = formData.liveDescription;
      }

      createMutation.mutate(submitData);
      // Close modal immediately - optimistic update already shows schedule
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
      <div className="rounded-2xl max-w-md w-full p-4 relative" style={{ maxHeight: '98vh', overflowY: 'auto', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}>
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors"
          type="button"
          aria-label="Close"
        >
          <X className="w-6 h-6" />
        </button>
        <h2 className="text-xl font-bold text-[var(--text-primary)] mb-2">Schedule Class</h2>

        {/* Class Type Toggle */}
        <div className="flex gap-2 mb-4">
          <button
            type="button"
            className="flex-1 px-4 py-2 rounded-lg font-medium transition-colors"
            style={{
              backgroundColor: classType === 'video' ? 'var(--secondary)' : 'var(--bg-elevated)',
              color: classType === 'video' ? '#ffffff' : 'var(--text-secondary)',
              border: classType === 'video' ? '2px solid var(--secondary)' : '2px solid var(--border-subtle)'
            }}
            onClick={() => setClassType('video')}
          >
            Video Class
          </button>
          <button
            type="button"
            className="flex-1 px-4 py-2 rounded-lg font-medium transition-colors"
            style={{
              backgroundColor: classType === 'live' ? 'var(--primary)' : 'var(--bg-elevated)',
              color: classType === 'live' ? '#ffffff' : 'var(--text-secondary)',
              border: classType === 'live' ? '2px solid var(--primary)' : '2px solid var(--border-subtle)'
            }}
            onClick={() => setClassType('live')}
          >
            Live Class
          </button>
        </div>

        {/* Video library info - only shown for video classes */}
        {classType === 'video' && videos && videos.length > 0 && (
          <div className="text-xs text-[var(--text-secondary)] mb-2 p-2 bg-[var(--highlight-5)] rounded-lg">
            <p className="font-medium mb-1">
              Found {videos.length} total video{videos.length !== 1 ? 's' : ''} in library.
            </p>
            <p className="text-xs text-[var(--text-muted)]">
              Available categories: {[...new Set(videos.map(v => v.category))].join(', ')}
            </p>
          </div>
        )}

        {/* Live class info */}
        {classType === 'live' && (
          <div className="text-xs mb-2 p-2 rounded-lg" style={{ backgroundColor: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)' }}>
            <p className="font-medium">
              Schedule an in-person class (no video playback)
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Video Class Fields */}
          {classType === 'video' && (
            <>
              <CustomDropdown
                value={selectedCategory}
                onChange={handleCategoryChange}
                options={categories.map(cat => ({ value: cat.value, label: `${cat.icon} ${cat.label}` }))}
                placeholder="Choose a category..."
                label="Select Category"
                required
              />

              {selectedCategory && (
                <>
                  {filteredVideos.length > 0 ? (
                    <CustomDropdown
                      value={formData.videoId}
                      onChange={(val) => setFormData({ ...formData, videoId: val })}
                      options={filteredVideos.map(video => {
                        const durationSeconds = video.duration || 0;
                        const minutes = Math.floor(durationSeconds / 60);
                        const seconds = durationSeconds % 60;
                        let durationText = '';
                        if (minutes > 0 && seconds > 0) {
                          durationText = `${minutes} min ${seconds} sec`;
                        } else if (minutes > 0) {
                          durationText = `${minutes} min`;
                        } else if (seconds > 0) {
                          durationText = `${seconds} sec`;
                        } else {
                          durationText = '0 min';
                        }
                        return {
                          value: video.id,
                          label: `${video.title} (${durationText})`
                        };
                      })}
                      placeholder="Choose a video..."
                      label="Select Video"
                      required
                    />
                  ) : (
                    <div>
                      <label className="block text-sm font-semibold text-[var(--text-primary)] mb-2">
                        Select Video
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <div className="text-sm text-[var(--text-secondary)] p-3 bg-[var(--highlight-5)] rounded-xl border-2 border-[var(--border-subtle)]">
                        <p className="font-medium">No videos available in this category.</p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* Live Class Fields */}
          {classType === 'live' && (
            <>
              <div>
                <label className="block text-sm font-semibold text-[var(--text-primary)] mb-2">
                  Class Title
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="text"
                  value={formData.liveTitle}
                  onChange={(e) => setFormData({ ...formData, liveTitle: e.target.value })}
                  className="w-full px-3 py-2 border-2 border-[var(--border-subtle)] rounded-lg focus:border-[var(--secondary)] focus:outline-none"
                  style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)' }}
                  placeholder="e.g., Morning Yoga Flow"
                  required
                />
              </div>

              <CustomDropdown
                value={formData.liveCategory}
                onChange={(val) => setFormData({ ...formData, liveCategory: val })}
                options={categories.map(cat => ({ value: cat.value, label: `${cat.icon} ${cat.label}` }))}
                placeholder="Choose a category..."
                label="Category"
                required
              />

              <div>
                <label className="block text-sm font-semibold text-[var(--text-primary)] mb-2">
                  Instructor Name
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="text"
                  value={formData.liveInstructorName}
                  onChange={(e) => setFormData({ ...formData, liveInstructorName: e.target.value })}
                  className="w-full px-3 py-2 border-2 border-[var(--border-subtle)] rounded-lg focus:border-[var(--secondary)] focus:outline-none"
                  style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)' }}
                  placeholder="e.g. John Smith"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[var(--text-primary)] mb-2">
                  Duration (minutes)
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="number"
                  min="1"
                  max="480"
                  value={formData.liveDuration}
                  onChange={(e) => setFormData({ ...formData, liveDuration: parseInt(e.target.value) || 60 })}
                  className="w-full px-3 py-2 border-2 border-[var(--border-subtle)] rounded-lg focus:border-[var(--secondary)] focus:outline-none"
                  style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)' }}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[var(--text-primary)] mb-2">
                  Description (optional)
                </label>
                <textarea
                  value={formData.liveDescription}
                  onChange={(e) => setFormData({ ...formData, liveDescription: e.target.value })}
                  className="w-full px-3 py-2 border-2 border-[var(--border-subtle)] rounded-lg focus:border-[var(--secondary)] focus:outline-none"
                  style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)' }}
                  placeholder="Brief description of the class..."
                  rows={2}
                />
              </div>
            </>
          )}

          <DatePicker
            value={formData.specificDate}
            onChange={(val) => setFormData({ ...formData, specificDate: val })}
            label="Date"
            required
          />

          <TimePicker
            value={formData.startTime}
            onChange={(val) => setFormData({ ...formData, startTime: val })}
            label="Start Time"
            required
          />

          {/* Recurring Schedule Options */}
          <div className="space-y-1.5 p-2 rounded-lg border-2 border-[var(--border-subtle)]" style={{ background: 'var(--bg-elevated)' }}>
            <label className="text-sm font-semibold text-[var(--text-primary)]">Recurrence</label>

            <CustomDropdown
              value={formData.recurrenceType}
              onChange={(val) => setFormData({
                ...formData,
                recurrenceType: val,
                daysOfWeek: val === 'daily' || val === 'weekly' ? [0, 1, 2, 3, 4, 5, 6] : formData.daysOfWeek
              })}
              options={[
                { value: 'once', label: 'Does not repeat' },
                { value: 'daily', label: 'Daily' },
                { value: 'weekly', label: 'Weekly' },
                { value: 'monthly', label: 'Monthly' }
              ]}
              placeholder="Select recurrence pattern..."
              openUpward={true}
            />

            {/* Daily: Days of Week Selection */}
            {formData.recurrenceType === 'daily' && (
              <div className="flex items-center gap-0.5">
                <span className="text-xs font-medium text-[var(--text-secondary)] mr-1">On:</span>
                {[
                  { value: 0, label: 'Sun' },
                  { value: 1, label: 'Mon' },
                  { value: 2, label: 'Tue' },
                  { value: 3, label: 'Wed' },
                  { value: 4, label: 'Thu' },
                  { value: 5, label: 'Fri' },
                  { value: 6, label: 'Sat' }
                ].map(day => {
                  const isSelected = formData.daysOfWeek.includes(day.value);
                  return (
                    <button
                      key={day.value}
                      type="button"
                      onClick={() => {
                        const newDays = formData.daysOfWeek.includes(day.value)
                          ? formData.daysOfWeek.filter(d => d !== day.value)
                          : [...formData.daysOfWeek, day.value].sort();
                        setFormData({ ...formData, daysOfWeek: newDays });
                      }}
                      className={`w-[48px] h-[24px] rounded-full text-[10px] font-semibold transition-all flex items-center justify-center gap-0.5 ${
                        isSelected
                          ? 'bg-primary-600 text-white'
                          : 'text-[var(--text-secondary)] border border-[var(--border-subtle)] hover:border-primary-400'
                      }`}
                      style={{ background: isSelected ? undefined : 'var(--bg-elevated)' }}
                    >
                      <span>{day.label}</span>
                      {isSelected && <Check className="w-2.5 h-2.5" />}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Weekly: Interval selection (compact) */}
            {formData.recurrenceType === 'weekly' && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-[var(--text-secondary)]">Every</span>
                <input
                  type="number"
                  min="1"
                  max="52"
                  value={formData.weeklyInterval}
                  onChange={(e) => setFormData({ ...formData, weeklyInterval: parseInt(e.target.value) || 1 })}
                  className="w-14 px-2 py-1 text-sm border border-[var(--border-subtle)] rounded focus:ring-1 focus:ring-primary-400"
                  style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)' }}
                />
                <span className="text-xs text-[var(--text-secondary)]">week(s) on {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][parseISO(formData.specificDate).getDay()]}</span>
              </div>
            )}

            {/* Monthly: Interval + Type Selection (compact) */}
            {formData.recurrenceType === 'monthly' && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[var(--text-secondary)]">Every</span>
                  <input
                    type="number"
                    min="1"
                    max="12"
                    value={formData.monthlyInterval}
                    onChange={(e) => setFormData({ ...formData, monthlyInterval: parseInt(e.target.value) || 1 })}
                    className="w-14 px-2 py-1 text-sm border border-[var(--border-subtle)] rounded focus:ring-1 focus:ring-primary-400"
                  style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)' }}
                  />
                  <span className="text-xs text-[var(--text-secondary)]">month(s)</span>
                </div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center cursor-pointer">
                    <input type="radio" name="monthlyType" value="date" checked={formData.monthlyType === 'date'} onChange={(e) => setFormData({ ...formData, monthlyType: e.target.value })} className="w-3 h-3 text-[var(--text-secondary)]" />
                    <span className="ml-1 text-xs text-[var(--text-secondary)]">Day {format(parseISO(formData.specificDate), 'd')}</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input type="radio" name="monthlyType" value="weekday" checked={formData.monthlyType === 'weekday'} onChange={(e) => setFormData({ ...formData, monthlyType: e.target.value })} className="w-3 h-3 text-[var(--text-secondary)]" />
                    <span className="ml-1 text-xs text-[var(--text-secondary)]">{['1st', '2nd', '3rd', '4th', '5th'][Math.ceil(parseISO(formData.specificDate).getDate() / 7) - 1]} {format(parseISO(formData.specificDate), 'EEE')}</span>
                  </label>
                </div>
              </div>
            )}

            {/* End Type Selection - Compact horizontal layout */}
            {formData.recurrenceType !== 'once' && (
              <div className="pt-2 border-t border-[var(--border-subtle)]">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-medium text-[var(--text-secondary)]">Ends:</span>
                  <label className="flex items-center cursor-pointer">
                    <input type="radio" name="endType" checked={formData.endType === 'date'} onChange={() => setFormData({ ...formData, endType: 'date' })} className="w-3 h-3 text-[var(--text-secondary)]" />
                    <span className="ml-1 text-xs text-[var(--text-secondary)]">On date</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input type="radio" name="endType" checked={formData.endType === 'never'} onChange={() => setFormData({ ...formData, endType: 'never' })} className="w-3 h-3 text-[var(--text-secondary)]" />
                    <span className="ml-1 text-xs text-[var(--text-secondary)]">Never (1 year)</span>
                  </label>
                </div>
                {formData.endType === 'date' && (
                  <div className="mt-2">
                    <DatePicker value={formData.endDate || ''} onChange={(val) => setFormData({ ...formData, endDate: val })} required dropdownDirection="up" compact minDate={formData.specificDate} />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Only show auto-play option for video classes */}
          {classType !== 'live' && (
            <div className="flex items-center p-4 bg-[var(--highlight-5)] rounded-xl border-2 border-[var(--border-subtle)] hover:border-[var(--border-subtle)] transition-all duration-200">
              <input
                type="checkbox"
                id="autoPlay"
                checked={formData.autoPlay}
                onChange={(e) => setFormData({ ...formData, autoPlay: e.target.checked })}
                className="w-5 h-5 text-[var(--text-secondary)] border-2 border-primary-300 rounded focus:ring-2 focus:ring-primary-400 cursor-pointer"
              />
              <label htmlFor="autoPlay" className="ml-3 text-sm font-medium text-[var(--text-primary)] cursor-pointer select-none">
                Auto-play at scheduled time
              </label>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary flex-1 shadow-sm hover:shadow-md transition-all duration-300 py-3 text-base font-semibold"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || (classType === 'video' ? !formData.videoId : !formData.liveTitle)}
              className="flex-1 shadow-md hover:shadow-lg transition-all duration-300 py-3 text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
              style={classType === 'live'
                ? { backgroundColor: 'var(--primary)', color: '#ffffff' }
                : { backgroundColor: 'var(--secondary)', color: '#ffffff' }}
            >
              {createMutation.isPending ? 'Scheduling...' : (classType === 'video' ? 'Schedule Video' : 'Schedule Live Class')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Convert to Recurring Modal Component
function ConvertToRecurringModal({ schedule, onConfirm, onCancel, isConverting }) {
  const [recurrenceType, setRecurrenceType] = useState('weekly');
  const [daysOfWeek, setDaysOfWeek] = useState([]);
  const [weeklyInterval, setWeeklyInterval] = useState(1);
  const [monthlyInterval, setMonthlyInterval] = useState(1);
  const [monthlyType, setMonthlyType] = useState('date');
  const [yearlyInterval, setYearlyInterval] = useState(1);
  const [endType, setEndType] = useState('date');
  const [endDate, setEndDate] = useState('');

  // Initialize days of week from schedule date
  useEffect(() => {
    if (schedule?.specificDate) {
      const date = parseISO(schedule.specificDate);
      setDaysOfWeek([date.getDay()]);
    }
  }, [schedule]);

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate end date
    if (endType === 'date' && !endDate) {
      toast.error('Please select an end date or check "Repeat Indefinitely"');
      return;
    }

    // Build recurrence pattern based on type
    let recurrencePattern = {};
    const scheduleDate = parseISO(schedule.specificDate);

    if (recurrenceType === 'daily') {
      if (daysOfWeek.length === 0) {
        toast.error('Please select at least one day of the week');
        return;
      }
      recurrencePattern = {
        type: 'weekly',
        daysOfWeek: daysOfWeek,
        interval: 1
      };
    } else if (recurrenceType === 'weekly') {
      recurrencePattern = {
        type: 'weekly',
        daysOfWeek: [scheduleDate.getDay()],
        interval: weeklyInterval
      };
    } else if (recurrenceType === 'monthly') {
      if (monthlyType === 'date') {
        recurrencePattern = {
          type: 'monthly',
          monthlyType: 'date',
          dayOfMonth: scheduleDate.getDate(),
          interval: monthlyInterval
        };
      } else {
        const weekOfMonth = Math.ceil(scheduleDate.getDate() / 7);
        recurrencePattern = {
          type: 'monthly',
          monthlyType: 'weekday',
          weekdayPosition: weekOfMonth,
          weekday: scheduleDate.getDay(),
          interval: monthlyInterval
        };
      }
    } else if (recurrenceType === 'yearly') {
      recurrencePattern = {
        type: 'yearly',
        month: scheduleDate.getMonth(),
        dayOfMonth: scheduleDate.getDate(),
        interval: yearlyInterval
      };
    }

    onConfirm({
      recurrencePattern,
      seriesEndDate: endType === 'never' ? null : endDate
    });
  };

  const scheduleDate = schedule?.specificDate ? parseISO(schedule.specificDate) : new Date();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
      <div className="rounded-2xl max-w-lg w-full p-6 shadow-xl max-h-[90vh] overflow-y-auto" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'var(--recurring-10)' }}>
            <Repeat className="w-5 h-5" style={{ color: 'var(--recurring)' }} />
          </div>
          <h2 className="text-xl font-bold text-[var(--text-primary)]">Make Recurring</h2>
        </div>

        <p className="text-[var(--text-secondary)] mb-4">
          Convert this one-time class to a recurring series. The original class (with any existing bookings) will become the first instance.
        </p>

        <div className="p-3 rounded-lg mb-4" style={{ background: 'var(--bg-elevated)' }}>
          <p className="font-medium text-[var(--text-primary)]">
            {schedule?.classType === 'live' ? schedule?.liveTitle : (schedule?.video?.title || schedule?.archivedVideoTitle)}
          </p>
          <p className="text-sm text-[var(--text-secondary)]">
            {schedule?.specificDate ? format(scheduleDate, 'MMMM d, yyyy') : ''} at {schedule?.startTime ? formatTime12Hour(schedule.startTime) : ''}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Recurrence Type */}
          <div>
            <label className="text-sm font-medium text-[var(--text-secondary)] mb-2 block">Repeat</label>
            <select
              value={recurrenceType}
              onChange={(e) => setRecurrenceType(e.target.value)}
              className="w-full px-3 py-2 border-2 rounded-lg"
              style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)', borderColor: 'var(--border-subtle)' }}
            >
              <option value="daily">Daily (specific days)</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>

          {/* Daily: Days of Week Selection */}
          {recurrenceType === 'daily' && (
            <div>
              <label className="text-sm font-medium text-[var(--text-secondary)] mb-2 block">Repeat on</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 0, label: 'Sun' },
                  { value: 1, label: 'Mon' },
                  { value: 2, label: 'Tue' },
                  { value: 3, label: 'Wed' },
                  { value: 4, label: 'Thu' },
                  { value: 5, label: 'Fri' },
                  { value: 6, label: 'Sat' }
                ].map(day => {
                  const isSelected = daysOfWeek.includes(day.value);
                  return (
                    <button
                      key={day.value}
                      type="button"
                      onClick={() => {
                        const newDays = daysOfWeek.includes(day.value)
                          ? daysOfWeek.filter(d => d !== day.value)
                          : [...daysOfWeek, day.value].sort();
                        setDaysOfWeek(newDays);
                      }}
                      className="w-12 h-10 rounded-lg text-sm font-medium transition-all"
                      style={{
                        background: isSelected ? 'var(--recurring)' : 'var(--bg-elevated)',
                        color: isSelected ? 'white' : 'var(--text-secondary)',
                        border: isSelected ? 'none' : '1px solid var(--border-subtle)'
                      }}
                    >
                      {day.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Weekly: Interval */}
          {recurrenceType === 'weekly' && (
            <div>
              <label className="text-sm font-medium text-[var(--text-secondary)] mb-2 block">Repeat every</label>
              <div className="flex gap-2 items-center">
                <input
                  type="number"
                  min="1"
                  max="52"
                  value={weeklyInterval}
                  onChange={(e) => setWeeklyInterval(parseInt(e.target.value) || 1)}
                  className="w-20 px-3 py-2 border-2 rounded-lg"
                  style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)', borderColor: 'var(--border-subtle)' }}
                />
                <span className="text-[var(--text-secondary)]">week(s) on {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][scheduleDate.getDay()]}</span>
              </div>
            </div>
          )}

          {/* Monthly: Interval + Type */}
          {recurrenceType === 'monthly' && (
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-[var(--text-secondary)] mb-2 block">Repeat every</label>
                <div className="flex gap-2 items-center">
                  <input
                    type="number"
                    min="1"
                    max="12"
                    value={monthlyInterval}
                    onChange={(e) => setMonthlyInterval(parseInt(e.target.value) || 1)}
                    className="w-20 px-3 py-2 border-2 border-[var(--border-subtle)] rounded-lg"
                    style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)' }}
                  />
                  <span className="text-[var(--text-secondary)]">month(s)</span>
                </div>
              </div>
              <div className="space-y-2">
                <label className="flex items-center p-2 rounded-lg hover:bg-[var(--highlight-5)] cursor-pointer">
                  <input
                    type="radio"
                    name="monthlyType"
                    value="date"
                    checked={monthlyType === 'date'}
                    onChange={(e) => setMonthlyType(e.target.value)}
                    className="w-4 h-4"
                    style={{ accentColor: 'var(--recurring)' }}
                  />
                  <span className="ml-2 text-sm text-[var(--text-secondary)]">On day {format(scheduleDate, 'd')} of month</span>
                </label>
                <label className="flex items-center p-2 rounded-lg hover:bg-[var(--highlight-5)] cursor-pointer">
                  <input
                    type="radio"
                    name="monthlyType"
                    value="weekday"
                    checked={monthlyType === 'weekday'}
                    onChange={(e) => setMonthlyType(e.target.value)}
                    className="w-4 h-4"
                    style={{ accentColor: 'var(--recurring)' }}
                  />
                  <span className="ml-2 text-sm text-[var(--text-secondary)]">
                    On {['first', 'second', 'third', 'fourth', 'fifth'][Math.ceil(scheduleDate.getDate() / 7) - 1]} {format(scheduleDate, 'EEEE')}
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* Yearly: Interval */}
          {recurrenceType === 'yearly' && (
            <div>
              <label className="text-sm font-medium text-[var(--text-secondary)] mb-2 block">Repeat every</label>
              <div className="flex gap-2 items-center">
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={yearlyInterval}
                  onChange={(e) => setYearlyInterval(parseInt(e.target.value) || 1)}
                  className="w-20 px-3 py-2 border-2 border-[var(--border-subtle)] rounded-lg"
                  style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)' }}
                />
                <span className="text-[var(--text-secondary)]">year(s) on {format(scheduleDate, 'MMMM d')}</span>
              </div>
            </div>
          )}

          {/* End Type */}
          <div className="pt-3 border-t border-[var(--border-subtle)]">
            <label className="flex items-center p-2 rounded-lg hover:bg-[var(--highlight-5)] cursor-pointer">
              <input
                type="checkbox"
                checked={endType === 'never'}
                onChange={(e) => setEndType(e.target.checked ? 'never' : 'date')}
                className="w-4 h-4 rounded"
                style={{ accentColor: 'var(--recurring)' }}
              />
              <span className="ml-2 text-sm font-medium text-[var(--text-secondary)]">Repeat indefinitely</span>
            </label>

            {endType !== 'never' && (
              <div className="mt-3">
                <DatePicker
                  value={endDate}
                  onChange={setEndDate}
                  label="Until date"
                  required
                  dropdownDirection="up"
                  minDate={schedule?.specificDate}
                />
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              disabled={isConverting}
              className="flex-1 btn bg-[var(--highlight-10)] hover:bg-[var(--highlight-15)] text-[var(--text-secondary)] py-3 text-base font-semibold rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isConverting}
              className="flex-1 btn text-white py-3 text-base font-semibold flex items-center justify-center gap-2 rounded-lg"
              style={{ background: 'var(--recurring)' }}
            >
              {isConverting ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Converting...
                </>
              ) : (
                <>
                  <Repeat className="w-5 h-5" />
                  Make Recurring
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
